import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import PaginationComponent from "../../../components/common/Pagination/PaginationComponent";
import LineChart from "../../../components/Dashboard/Charts/LineChart/LineChart";
import HorizontalBarChart from "../../../components/Dashboard/Charts/BarChart/HorizontalBarChart";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import classes from "../../TabStyles/DashboardTab.module.css";
import { API_ENDPOINT } from "../../../services/api/resources";
import { nextPageClick, previouPageClick } from "../../../utils/pagination";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

const BUYER_ANALYTICS_MENU = [
  {
    id: "dashboard1",
    label: "Procurement Operations Control Tower",
    description: "Executive workflow health and command metrics",
    accent: "#2563EB",
  },
  {
    id: "dashboard2",
    label: "Enterprise Spend Analytics",
    description: "Budget vs actual spend and allocation views",
    accent: "#0EA5E9",
  },
  {
    id: "dashboard3",
    label: "Savings & Avoided Cost Analytics",
    description: "Savings breakdown across categories and vendors",
    accent: "#10B981",
  },
  {
    id: "dashboard4",
    label: "Sourcing Intelligence Center",
    description: "RFQ flow and vendor participation",
    accent: "#F59E0B",
  },
  {
    id: "dashboard5",
    label: "Supplier Performance Scorecard",
    description: "Delivery, quality and ranking metrics",
    accent: "#7C3AED",
  },
  {
    id: "dashboard6",
    label: "Autonomous Procurement Control Center",
    description: "Touchless execution and automation KPIs",
    accent: "#7C3AED",
  },
  {
    id: "dashboard7",
    label: "Purchase Price Benchmarking Intelligence",
    description: "Price movement and PO comparison insights",
    accent: "#A855F7",
  },
  {
    id: "dashboard8",
    label: "Procurement Insights",
    description: "Asset health, warranty split, and total book value",
    accent: "#14B8A6",
  },
  {
    id: "dashboard9",
    label: "PO Delivery Tracker",
    description: "Delivery status, fulfilment and overdue purchase orders",
    accent: "#0F766E",
  },
];

const T = {
  bg: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceAlt: "#FFFFFF",
  text: "#0F172A",
  heading: "#1a51d1",
  textSec: "#334155",
  textMuted: "#64748B",
  border: "rgba(226,232,240,0.7)",
  brand: "#4F7CFF",
  success: "#24C8A5",
  warning: "#F5A524",
  danger: "#EF4444",
  purple: "#7C3AED",
  info: "#2AA7E0",
  fontBody: "gilroyRegular",
  fontDisplay: "gilroyBold",
};

const NON_ADDRESSABLE_CATEGORY_KEYWORDS = [
  "office rent",
  "office electricity",
  "house keeping",
  "housekeeping",
  "security source",
  "security services",
  "security service",
  "office maintenance",
  "office manitance",
  "office maintanance",
  "office maitainance",
  "hvac maintenance",
  "hpac maintenance",
  "genset maintenance",
  "diesel",
  "office stationary",
  "office stationery",
  "property tax",
  "water tax",
  "drinking water",
];

const DASHBOARD_MODULE_ROUTES = {
  pr: "/Buyer_dashboard/PR",
  rfq: "/Buyer_dashboard/RFQ",
  po: "/Buyer_dashboard/PO",
  invoice: "/Buyer_dashboard/Invoice",
  matching: "/Buyer_dashboard/Matching",
  grn: "/GRN_Dashboard/GRN",
  vendor: "/Buyer_dashboard/vendors",
  supplier: "/Buyer_dashboard/vendors",
  item: "/Buyer_dashboard/Items",
  asset: "/Buyer_dashboard/Asset_Register",
  rateContract: "/Buyer_dashboard/rate_contract",
  priceList: "/Buyer_dashboard/price_list",
  inventory: "/Buyer_dashboard/Inventory_Master",
  sourcing: "/Buyer_dashboard/Sourcing_Intelligence",
  approval: "/Buyer_dashboard/vendor_approval",
};

const resolveDashboardRoute = (value) => {
  const text = normalizeCategoryText(value);
  if (!text) return "";

  if (/\b3\s*way\b/.test(text) || text.includes("matching")) return DASHBOARD_MODULE_ROUTES.matching;
  if (/\bgrn\b/.test(text)) return DASHBOARD_MODULE_ROUTES.grn;
  if (text.includes("invoice")) return DASHBOARD_MODULE_ROUTES.invoice;
  if (/\bpo\b/.test(text) || text.includes("purchase order") || text.includes("addressable spend")) return DASHBOARD_MODULE_ROUTES.po;
  if (/\bpr\b/.test(text) || text.includes("purchase requisition") || text.includes("budget")) return DASHBOARD_MODULE_ROUTES.pr;
  if (/\brfq\b/.test(text) || text.includes("quotation") || text.includes("quote") || text.includes("sourcing")) return DASHBOARD_MODULE_ROUTES.rfq;
  if (text.includes("vendor") || text.includes("supplier")) return DASHBOARD_MODULE_ROUTES.vendor;
  if (text.includes("asset") || text.includes("warranty") || text.includes("book value")) return DASHBOARD_MODULE_ROUTES.asset;
  if (text.includes("rate contract") || text.includes("contracted")) return DASHBOARD_MODULE_ROUTES.rateContract;
  if (text.includes("price") || text.includes("benchmark")) return DASHBOARD_MODULE_ROUTES.priceList;
  if (text.includes("inventory")) return DASHBOARD_MODULE_ROUTES.inventory;
  if (text.includes("item") || text.includes("category")) return DASHBOARD_MODULE_ROUTES.item;
  if (text.includes("approval") || text.includes("approved") || text.includes("rejected")) return DASHBOARD_MODULE_ROUTES.approval;

  return "";
};

const normalizeCategoryText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const isNonAddressablePurchaseMovementItem = (row = {}) => {
  const haystack = normalizeCategoryText(
    [
      row?.item?.name,
      row?.item?.code,
      row?.item?.category,
      row?.category,
    ]
      .filter(Boolean)
      .join(" ")
  );

  return NON_ADDRESSABLE_CATEGORY_KEYWORDS.some((keyword) => haystack.includes(normalizeCategoryText(keyword)));
};

const SectionCard = ({ title, children }) => (
  <div
    style={{
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: 20,
      boxShadow: "0px 8px 30px rgba(15,23,42,0.06)",
      padding: "28px 28px 30px",
      marginBottom: 20,
      position: "relative",
      overflow: "visible",
      transition: "transform 180ms ease, box-shadow 180ms ease",
    }}
  >
    <SubSectionHeader title={title} />
    {children}
  </div>
);

const SubSectionHeader = ({ title }) => (
  <div
    style={{
      fontSize: 18,
      fontFamily: T.fontDisplay,
      color: T.text,
      marginBottom: 16,
      paddingBottom: 12,
      borderBottom: `1px solid rgba(148,163,184,0.18)`,
      display: "flex",
      alignItems: "center",
      gap: 10,
    }}
  >
    {title}
  </div>
);

const Divider = () => <div style={{ height: 1, background: "rgba(148,163,184,0.16)", margin: "18px 0" }} />;

const chartHeaderTitleStyle = {
  fontSize: 18,
  fontFamily: T.fontDisplay,
  color: T.text,
  lineHeight: 1.2,
  minWidth: 0,
};

const getChartHeaderRowStyle = (isNarrowLayout) => ({
  display: "flex",
  alignItems: isNarrowLayout ? "stretch" : "center",
  justifyContent: "space-between",
  gap: 12,
  flexDirection: isNarrowLayout ? "column" : "row",
  marginBottom: 12,
  paddingBottom: 12,
  borderBottom: `1px solid rgba(148,163,184,0.18)`,
});

const DefaultMetricTooltip = ({ title, value, note }) => (
  <div style={{ display: "grid", gap: 10 }}>
    <div>
      <div style={{ fontSize: 12, fontFamily: T.fontDisplay, color: T.textMuted, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {title}
      </div>
      <div style={{ marginTop: 2, fontSize: 15, fontFamily: T.fontDisplay, color: T.text }}>
        {value} total
      </div>
      <div style={{ marginTop: 1, fontSize: 12, color: T.textMuted }}>
        Year-wise breakdown
      </div>
    </div>
    <div style={{ display: "grid", gap: 6, paddingRight: 4 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "6px 8px",
          borderRadius: 10,
          background: "rgba(248,250,252,0.9)",
        }}
      >
        <span style={{ color: T.textSec, fontSize: 13, fontFamily: T.fontDisplay }}>All years</span>
        <span style={{ color: T.text, fontSize: 13, fontFamily: T.fontDisplay }}>{value}</span>
      </div>
      {note ? <div style={{ color: T.textMuted, fontSize: 12 }}>{note}</div> : null}
    </div>
  </div>
);

const MetricCard = ({
  title,
  value,
  note,
  accent = T.brand,
  tooltip,
  valueColor,
  valueStyle = {},
  emphasis = false,
  cardStyle = {},
  onClick,
}) => {
  const cardRef = React.useRef(null);
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const tooltipContent = tooltip || <DefaultMetricTooltip title={title} value={value} note={note} />;
  const isOpen = hovered || pinned;
  const isInteractive = Boolean(tooltipContent || onClick);
  const handleActivate = useCallback(() => {
    if (onClick) onClick();
    else if (tooltipContent) setPinned((current) => !current);
  }, [onClick, tooltipContent]);

  useEffect(() => {
    if (!pinned) return undefined;

    const handlePointerDown = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        setPinned(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [pinned]);

  return (
    <div
      ref={cardRef}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={onClick ? `Open ${title}` : title}
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 20,
        padding: emphasis ? "24px 22px 22px" : "20px 20px 18px",
        minHeight: emphasis ? 142 : 118,
        boxShadow: emphasis ? "0px 16px 38px rgba(15,23,42,0.11)" : "0px 8px 30px rgba(15,23,42,0.06)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        overflow: "visible",
        transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        cursor: isInteractive ? "pointer" : "default",
        ...cardStyle,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleActivate}
      onKeyDown={(event) => {
        if (!isInteractive || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        handleActivate();
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${accent}, rgba(255,255,255,0.55))`,
          borderRadius: "20px 20px 0 0",
          pointerEvents: "none",
        }}
      />
      <div style={{ paddingTop: 6 }}>
        <div
          style={{
            fontSize: 16,
            fontFamily: T.fontDisplay,
            color: T.textSec,
            lineHeight: 1.25,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: emphasis ? "clamp(24px, 1.65vw, 32px)" : "clamp(18px, 1.25vw, 24px)",
            color: valueColor || T.text,
            lineHeight: 1.15,
            marginTop: 6,
            letterSpacing: 0,
            fontFamily: emphasis ? T.fontDisplay : T.fontBody,
            wordBreak: "break-word",
            ...valueStyle,
          }}
        >
          {value}
        </div>
      </div>
      {note ? (
        <div style={{ fontSize: 14, fontFamily: T.fontBody, color: T.textMuted, marginTop: 8 }}>
          {note}
        </div>
      ) : null}
      {onClick ? (
        <div
          style={{
            marginTop: 12,
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 9px",
            borderRadius: 999,
            background: hexToRgba(accent, 0.1),
            border: `1px solid ${hexToRgba(accent, 0.16)}`,
            color: accent,
            fontSize: 12,
            fontFamily: T.fontDisplay,
            lineHeight: 1,
          }}
        >
          Open
          <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>-&gt;</span>
        </div>
      ) : null}
      {tooltipContent ? (
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            position: "absolute",
            left: 14,
            right: 14,
            top: "calc(100% + 10px)",
            background: "#FFFFFF",
            border: "1px solid rgba(226,232,240,0.95)",
            borderRadius: 14,
            boxShadow: "0px 18px 36px rgba(15,23,42,0.14)",
            padding: "12px 14px",
            minWidth: 340,
            width: "max-content",
            maxWidth: "min(460px, calc(100vw - 48px))",
            maxHeight: 260,
            overflowY: "auto",
            color: T.text,
            fontFamily: T.fontBody,
            fontSize: 13,
            lineHeight: 1.45,
            opacity: isOpen ? 1 : 0,
            transform: isOpen ? "translateY(0)" : "translateY(-4px)",
            transition: "opacity 180ms ease, transform 180ms ease",
            pointerEvents: isOpen ? "auto" : "none",
            zIndex: 20,
          }}
        >
          {tooltipContent}
        </div>
      ) : null}
    </div>
  );
};

const NestedCard = ({ children, style = {} }) => (
  <div
    style={{
      background: T.surfaceAlt,
      border: `1px solid ${T.border}`,
      borderRadius: 20,
      padding: 24,
      minWidth: 0,
      boxShadow: "0px 8px 30px rgba(15,23,42,0.05)",
      transition: "transform 180ms ease, box-shadow 180ms ease",
      ...style,
    }}
  >
    {children}
  </div>
);

const normalizeMetricValue = (value) => {
  if (typeof value === "number") return value;
  const numeric = Number(String(value ?? "").replace(/,/g, "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeLocationPrValue = (value) => {
  if (typeof value === "number") return value;

  const rawValue = String(value ?? "").trim();
  if (/^-?\d+,\d{1,3}(?:\s+[A-Z]{3})?$/i.test(rawValue)) {
    const decimalValue = Number(rawValue.replace(/\s+[A-Z]{3}$/i, "").replace(",", "."));
    return Number.isFinite(decimalValue) ? decimalValue : 0;
  }

  return normalizeMetricValue(value);
};

const normalizePrLocationLabel = (value) => {
  const parts = String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part && !["null", "undefined", "unknown"].includes(part.toLowerCase()));

  return parts.length ? parts.join(", ") : "Unmapped";
};

const mapArrayToChart = (items = [], labelKey = "name", valueKey = "amount", limit = Infinity) => {
  const safe = Array.isArray(items) ? items.slice(0, limit) : [];
  return {
    labels: safe.map((item) => String(item?.[labelKey] ?? "").trim()).filter(Boolean),
    values: safe.map((item) => normalizeMetricValue(item?.[valueKey])),
  };
};

const sortObjectEntries = (object = {}) =>
  Object.entries(object || {}).sort(([a], [b]) => String(a).localeCompare(String(b)));

const objectToSeriesChart = (object = {}) => {
  const entries = sortObjectEntries(object);
  return {
    labels: entries.map(([label]) => label),
    values: entries.map(([, value]) => normalizeMetricValue(value)),
  };
};

const MONTH_OPTIONS = [
  { value: "all", label: "All Months" },
  { value: "01", label: "Jan" },
  { value: "02", label: "Feb" },
  { value: "03", label: "Mar" },
  { value: "04", label: "Apr" },
  { value: "05", label: "May" },
  { value: "06", label: "Jun" },
  { value: "07", label: "Jul" },
  { value: "08", label: "Aug" },
  { value: "09", label: "Sep" },
  { value: "10", label: "Oct" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Dec" },
];

const getTrendYears = (object = {}) =>
  Array.from(
    new Set(
      Object.keys(object || {})
        .map((key) => String(key).split("-")[0])
        .filter(Boolean)
    )
  ).sort();

const filterTrendByPeriod = (object = {}, { year = "all", month = "all" } = {}) => {
  const entries = sortObjectEntries(object);
  const filtered = entries.filter(([key]) => {
    const [keyYear, keyMonth] = String(key).split("-");
    if (year !== "all" && String(keyYear) !== String(year)) return false;
    if (month !== "all" && String(keyMonth) !== String(month)) return false;
    return true;
  });

  return {
    labels: filtered.map(([label]) => label),
    values: filtered.map(([, value]) => normalizeMetricValue(value)),
  };
};

const keyToLabel = (key) =>
  String(key ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "0";
  if (typeof value === "number") return String(value);
  return String(value);
};

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const normalizeLookupKey = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const getObjectValueByKey = (source, key) => {
  if (!isPlainObject(source)) return undefined;
  const normalizedKey = normalizeLookupKey(key);
  const entry = Object.entries(source).find(([candidateKey]) => normalizeLookupKey(candidateKey) === normalizedKey);
  return entry?.[1];
};

const getNestedObjectValue = (source, ...pathCandidates) => {
  for (const candidate of pathCandidates.filter(Boolean)) {
    const path = Array.isArray(candidate) ? candidate : [candidate];
    let current = source;
    let resolved = true;

    for (const key of path) {
      current = getObjectValueByKey(current, key);
      if (current === undefined) {
        resolved = false;
        break;
      }
    }

    if (resolved && current !== undefined) {
      return current;
    }
  }

  return undefined;
};

const scalarEntries = (object = {}, exclude = []) =>
  Object.entries(object || {}).filter(
    ([key, value]) => !exclude.includes(key) && !Array.isArray(value) && !isPlainObject(value)
  );

const groupTopRows = (rows = [], { labelKey = "name", valueKey = "amount", limit = 8 } = {}) => {
  const list = Array.isArray(rows)
    ? rows
        .map((item) => ({
          ...item,
          __label: String(item?.[labelKey] ?? "").trim(),
          __value: normalizeMetricValue(item?.[valueKey]),
        }))
        .filter((item) => item.__label)
        .sort((a, b) => b.__value - a.__value)
    : [];

  if (list.length <= limit) return list;

  const top = list.slice(0, limit);
  const othersItems = list.slice(limit);
  const othersValue = othersItems.reduce((sum, item) => sum + item.__value, 0);
  return [
    ...top,
    {
      __label: "Others",
      __value: othersValue,
      __isOthers: true,
      __othersItems: othersItems.map((item) => ({
        label: item.__label,
        value: item.__value,
      })),
    },
  ];
};

const formatChartValue = (value) => {
  const numeric = normalizeMetricValue(value);
  if (!Number.isFinite(numeric)) return "0";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(numeric);
};

const formatCurrencyCompact = (value) => {
  const numeric = normalizeMetricValue(value);
  const absolute = Math.abs(numeric);
  const sign = numeric < 0 ? "-" : "";

  if (!Number.isFinite(numeric) || numeric === 0) return "\u20B90";
  if (absolute >= 10000000) return `${sign}\u20B9${(absolute / 10000000).toFixed(1)} Cr`;
  if (absolute >= 100000) return `${sign}\u20B9${(absolute / 100000).toFixed(1)} L`;
  return `${sign}\u20B9${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(absolute)}`;
};

const formatCurrencyRoundedCr = (value) => {
  const numeric = normalizeMetricValue(value);
  const absolute = Math.abs(numeric);
  const sign = numeric < 0 ? "-" : "";

  if (!Number.isFinite(numeric) || numeric === 0) return "\u20B90";
  if (absolute >= 10000000) return `${sign}\u20B9${(absolute / 10000000).toFixed(1).replace(/\.0$/, "")}cr`;
  if (absolute >= 100000) return `${sign}\u20B9${(absolute / 100000).toFixed(1).replace(/\.0$/, "")}l`;
  return `${sign}\u20B9${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(absolute)}`;
};

const formatPercentCompact = (value) => `${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 }).format(Math.max(0, normalizeMetricValue(value)))}%`;

const safeToken = (value) => String(value ?? "").trim();

const buildGradientColor = (ctx, startColor, endColor, vertical = false) => {
  const chart = ctx?.chart;
  const area = chart?.chartArea;
  if (!chart || !area) return startColor;
  const { ctx: canvasCtx } = chart;
  const gradient = vertical
    ? canvasCtx.createLinearGradient(0, area.bottom, 0, area.top)
    : canvasCtx.createLinearGradient(area.left, 0, area.right, 0);
  gradient.addColorStop(0, endColor || startColor);
  gradient.addColorStop(1, startColor);
  return gradient;
};

const softPalette = (baseColor, count = 6) => {
  const palette = [baseColor, T.info, T.success, T.warning, T.purple, "#60A5FA"];
  return Array.from({ length: count }, (_, index) => palette[index % palette.length]);
};

const lightenHex = (hex, amount = 0.15) => {
  const normalized = String(hex || "").replace("#", "");
  if (normalized.length !== 6) return hex;
  const n = parseInt(normalized, 16);
  const r = Math.round(((n >> 16) & 255) + (255 - ((n >> 16) & 255)) * amount);
  const g = Math.round(((n >> 8) & 255) + (255 - ((n >> 8) & 255)) * amount);
  const b = Math.round((n & 255) + (255 - (n & 255)) * amount);
  return `rgb(${r}, ${g}, ${b})`;
};

const hexToRgba = (hex, alpha = 1) => {
  const normalized = String(hex || "").replace("#", "");
  if (normalized.length !== 6) return hex;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getRechartsSeries = (rows = [], { labelKey = "name", valueKey = "amount", limit = 8, othersLabel = "Others" } = {}) => {
  const grouped = groupTopRows(rows, { labelKey, valueKey, limit });
  return grouped.map((item) => ({
    label: item.__label,
    value: item.__value,
    isOthers: Boolean(item.__isOthers),
  }));
};

const splitTickLabel = (label = "", max = 14) => {
  const words = String(label ?? "").split(/\s+/).filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > max && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, 2);
};

const formatTooltipValue = (value) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Math.abs(Number(value) || 0));

const formatCurrencyInCrores = (value) => {
  const numeric = normalizeMetricValue(value);
  const absolute = Math.abs(numeric);
  if (!Number.isFinite(numeric) || numeric === 0) return "₹0 Cr";

  const crores = absolute / 10000000;
  const formatted = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(crores);

  return `₹${formatted} Cr`;
};

const renderTooltipBreakdown = (items = [], formatter = formatTooltipValue) => {
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <div
      style={{
        marginTop: 8,
        paddingTop: 8,
        borderTop: "1px solid rgba(226,232,240,0.92)",
        display: "grid",
        gap: 6,
        maxHeight: 180,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: T.textMuted,
          fontFamily: T.fontBody,
          letterSpacing: "0.01em",
        }}
      >
        Remaining breakdown
      </div>
      {items.map((entry, index) => (
        <div
          key={`${entry?.label || "item"}-${index}`}
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            gap: 12,
            alignItems: "start",
          }}
        >
          <div
            style={{
              fontSize: 12.5,
              color: T.text,
              fontFamily: T.fontBody,
              lineHeight: 1.35,
              overflowWrap: "anywhere",
            }}
          >
            {entry?.label || "-"}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: T.text,
              fontFamily: T.fontDisplay,
              whiteSpace: "nowrap",
            }}
          >
            {formatter(entry?.value ?? 0)}
          </div>
        </div>
      ))}
    </div>
  );
};

const getViewportSafePosition = (
  x,
  y,
  width,
  height,
  {
    offsetX = 18,
    offsetY = -12,
    padding = 12,
  } = {}
) => {
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth || 0 : 0;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight || 0 : 0;

  const preferredLeft = x + offsetX;
  const preferredTop = y + offsetY;

  return {
    left: Math.max(
      padding,
      Math.min(preferredLeft, Math.max(padding, viewportWidth - width - padding))
    ),
    top: Math.max(
      padding,
      Math.min(preferredTop, Math.max(padding, viewportHeight - height - padding))
    ),
  };
};

const CustomChartTooltip = ({
  active,
  payload,
  label,
  title,
  showPercent = false,
  total = 0,
  valueFormatter = formatTooltipValue,
  breakdownValueFormatter = valueFormatter,
}) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const value = Number(item?.value || 0);
  const percent = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
  const sourceRow = item?.payload || {};
  const othersItems = Array.isArray(sourceRow?.__othersItems)
    ? sourceRow.__othersItems
    : [];

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid rgba(226,232,240,0.92)",
        boxShadow: "0px 12px 30px rgba(15,23,42,0.12)",
        borderRadius: 12,
        padding: "10px 12px",
        fontFamily: T.fontBody,
        fontSize: 13,
        color: T.text,
        minWidth: 180,
        maxWidth: 340,
      }}
    >
      <div style={{ color: T.textMuted, marginBottom: 4, fontSize: 12.5 }}>
        {label || title || item?.name || ""}
      </div>
      <div style={{ fontFamily: T.fontDisplay, fontSize: 13 }}>
        {valueFormatter(value)}
        {showPercent ? <span style={{ color: T.textMuted, fontFamily: T.fontBody }}> ({percent}%)</span> : null}
      </div>
      {renderTooltipBreakdown(othersItems, breakdownValueFormatter)}
    </div>
  );
};

const RechartsLegend = ({ rows = [], colors = [] }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "10px 14px",
      marginTop: 14,
      justifyContent: "center",
    }}
  >
    {rows.map((item, index) => (
      <div
        key={`${item.label}-${index}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: colors[index % colors.length],
            flexShrink: 0,
          }}
        />
        <span
          title={item.label}
          style={{
            fontSize: 13,
            fontFamily: T.fontBody,
            color: T.textMuted,
            lineHeight: 1.35,
            maxWidth: 180,
            whiteSpace: "normal",
            overflowWrap: "anywhere",
          }}
        >
          {item.label}
        </span>
      </div>
    ))}
  </div>
);

const RechartsWrappedTick = ({ x, y, payload, width = 140, dy = 14, fill = T.textMuted }) => {
  const lines = splitTickLabel(payload?.value, Math.max(12, Math.floor(width / 10)));
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="middle"
        fill={fill}
        fontFamily={T.fontBody}
        fontSize={14}      >
        {lines.map((line, index) => (
          <tspan key={index} x={0} dy={index === 0 ? dy : 16}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const RechartsAngledTick = ({ x, y, payload, fill = T.textMuted, angle = -28 }) => (
  <g transform={`translate(${x},${y})`}>
    <text
      transform={`rotate(${angle})`}
      x={0}
      y={14}
      textAnchor="end"
      fill={fill}
      fontFamily={T.fontBody}
      fontSize={12}    >
      {String(payload?.value ?? "")}
    </text>
  </g>
);

const RechartsWrappedYAxisTick = ({ x, y, payload, width = 150, fill = T.textMuted }) => {
  const lines = splitTickLabel(payload?.value, Math.max(12, Math.floor(width / 10)));
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={-8}
        y={0}
        textAnchor="end"
        fill={fill}
        fontFamily={T.fontBody}
        fontSize={14}      >
        {lines.map((line, index) => (
          <tspan key={index} x={-8} dy={index === 0 ? 5 : 16}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const NewBuyerDashboardV2 = ({
  dashboardResponse = {},
  analyticsData = {},
  loadingState,
  navigate,
  spendItems = [],
  spendTableHeadings = [],
  spendPaginationData,
  spendPageNumber = 1,
  setSpendPageNumber,
  spendSearchQuery = "",
  setSpendSearchQuery,
  spendTableLoading = false,
  spendSortingColumnName,
  spendSortingOrder,
  setSpendSortingColumnName,
  setSpendSortingOrder,
  disableAssetSummaryFetch = false,
}) => {
  const loginData = useSelector((state) => state.loginData.value);
  const authToken = loginData?.auth_token;
  const buyerToken =
    loginData?.buyer_token ||
    loginData?.user?.buyer_token ||
    loginData?.user?.token ||
    loginData?.organization_token ||
    loginData?.user_token;
  const orgToken =
    loginData?.org_token ||
    loginData?.organization_token ||
    loginData?.user?.org_token ||
    buyerToken;

  const [activeSection, setActiveSection] = useState("dashboard1");
  const tabNavScrollRef = useRef(null);
  const scrollDashboardTabs = (direction) => {
    tabNavScrollRef.current?.scrollBy({
      left: direction * Math.min(620, tabNavScrollRef.current.clientWidth * 0.75),
      behavior: "smooth",
    });
  };
  const [viewportWidth, setViewportWidth] = useState(0);
  const [prTrendYear, setPrTrendYear] = useState("all");
  const [prTrendMonth, setPrTrendMonth] = useState("all");
  const [rfqTrendYear, setRfqTrendYear] = useState("all");
  const [rfqTrendMonth, setRfqTrendMonth] = useState("all");
  const [poTrendYear, setPoTrendYear] = useState("all");
  const [poTrendMonth, setPoTrendMonth] = useState("all");
  const [purchaseMovementPageNumber, setPurchaseMovementPageNumber] = useState(1);
  const [purchaseMovementCategoryMode, setPurchaseMovementCategoryMode] = useState("addressable");
  const [pinnedOthersTooltip, setPinnedOthersTooltip] = useState(null);
  const [pinnedOthersTooltipPosition, setPinnedOthersTooltipPosition] = useState({
    left: 12,
    top: 12,
  });
  const [assetSummary, setAssetSummary] = useState(null);
  const [assetSummaryLoading, setAssetSummaryLoading] = useState(false);
  const [assetSummaryError, setAssetSummaryError] = useState("");
  const [deliverySummary, setDeliverySummary] = useState({});
  const [deliveryRows, setDeliveryRows] = useState([]);
  const [deliveryCount, setDeliveryCount] = useState(0);
  const [deliveryPage, setDeliveryPage] = useState(1);
  const [deliverySearch, setDeliverySearch] = useState("");
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState("");
  const [vendorDeliveryRows, setVendorDeliveryRows] = useState([]);
  const [supplierPerformancePage, setSupplierPerformancePage] = useState(1);
  const supplierPerformancePages = Math.max(1, Math.ceil(vendorDeliveryRows.length / 6));
  const currentSupplierPage = Math.min(supplierPerformancePage, supplierPerformancePages);
  const [vendorDeliveryLoading, setVendorDeliveryLoading] = useState(false);
  const [vendorDeliveryError, setVendorDeliveryError] = useState("");
  const assetSummaryFetchKeyRef = useRef("");
  const resolveCardNavigation = useCallback(
    (label) => {
      const route = resolveDashboardRoute(label);
      return route && typeof navigate === "function" ? () => navigate(route) : undefined;
    },
    [navigate]
  );
  const pinnedOthersTooltipRef = useRef(null);
  const dashboardRoot = dashboardResponse?.response || dashboardResponse || {};

  useEffect(() => {
    const updateViewport = () => setViewportWidth(window.innerWidth || 0);

    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    if (!pinnedOthersTooltip?.visible) return undefined;

    const updatePosition = () => {
      const node = pinnedOthersTooltipRef.current;
      if (!node) return;
      const { offsetWidth = 340, offsetHeight = 220 } = node;
      setPinnedOthersTooltipPosition(
        getViewportSafePosition(
          pinnedOthersTooltip.x,
          pinnedOthersTooltip.y,
          offsetWidth,
          offsetHeight
        )
      );
    };

    const frameId = window.requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updatePosition);
    };
  }, [pinnedOthersTooltip]);

  useEffect(() => {
    if (!pinnedOthersTooltip?.visible) return undefined;

    const handleOutsideClick = (event) => {
      if (pinnedOthersTooltipRef.current?.contains(event.target)) return;
      setPinnedOthersTooltip(null);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [pinnedOthersTooltip]);

  const isCompactLayout = viewportWidth > 0 && viewportWidth < 1200;
  const isNarrowLayout = viewportWidth > 0 && viewportWidth < 768;

  useEffect(() => {
    setPinnedOthersTooltip(null);
  }, [activeSection, purchaseMovementCategoryMode]);

  const sections = useMemo(
    () => ({
      dashboard1: dashboardRoot?.dashboard1 || {},
      dashboard2: dashboardRoot?.dashboard2 || {},
      dashboard3: dashboardRoot?.dashboard3 || {},
      dashboard4: dashboardRoot?.dashboard4 || {},
      dashboard5: dashboardRoot?.dashboard5 || {},
      dashboard6: dashboardRoot?.dashboard6 || {},
      dashboard7: dashboardRoot?.dashboard7 || {},
      dashboard8: dashboardRoot?.dashboard8 || {},
      budget_savings_analytics: dashboardRoot?.budget_savings_analytics || {},
      location_wise_pr_raised: dashboardRoot?.location_wise_pr_raised || [],
    }),
    [dashboardRoot]
  );

  useEffect(() => {
    if (activeSection !== "dashboard9" || !safeToken(orgToken)) return undefined;

    let active = true;
    const timer = window.setTimeout(async () => {
      setDeliveryLoading(true);
      setDeliveryError("");
      try {
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : undefined;
        const payload = { org_token: orgToken, page: deliveryPage, page_size: 10 };
        if (deliverySearch.trim()) payload.po_number = deliverySearch.trim();
        const [trackerResponse, summaryResponse] = await Promise.all([
          axios.post(API_ENDPOINT.delivery_tracker, payload, { headers }),
          axios.post(API_ENDPOINT.delivery_tracker_summary, { org_token: orgToken }, { headers }),
        ]);
        if (!active) return;
        const tracker = trackerResponse?.data?.response || trackerResponse?.data || {};
        const summary = summaryResponse?.data?.response || summaryResponse?.data || {};
        setDeliveryRows(Array.isArray(tracker?.results) ? tracker.results : []);
        setDeliveryCount(Number(tracker?.count || 0));
        setDeliverySummary(summary || {});
      } catch (error) {
        if (!active) return;
        setDeliveryRows([]);
        setDeliveryCount(0);
        setDeliveryError(error?.response?.data?.message || error?.message || "Unable to load delivery tracker");
      } finally {
        if (active) setDeliveryLoading(false);
      }
    }, deliverySearch ? 350 : 0);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [activeSection, authToken, deliveryPage, deliverySearch, orgToken]);

  useEffect(() => {
    if (activeSection !== "dashboard5" || !safeToken(orgToken)) return undefined;

    let active = true;
    setVendorDeliveryLoading(true);
    setVendorDeliveryError("");
    axios
      .post(
        API_ENDPOINT.delivery_tracker_vendors,
        { org_token: orgToken },
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      )
      .then((response) => {
        if (!active) return;
        const result = response?.data?.response || response?.data || [];
        setVendorDeliveryRows(Array.isArray(result) ? result : result?.results || []);
      })
      .catch((error) => {
        if (!active) return;
        setVendorDeliveryRows([]);
        setVendorDeliveryError(error?.response?.data?.message || error?.message || "Unable to load vendor delivery performance");
      })
      .finally(() => active && setVendorDeliveryLoading(false));

    return () => {
      active = false;
    };
  }, [activeSection, authToken, orgToken]);

  const fetchAssetSummary = useCallback(async () => {
    if (disableAssetSummaryFetch) {
      setAssetSummary(null);
      return;
    }

    if (!safeToken(buyerToken) || !safeToken(authToken)) {
      setAssetSummary(null);
      return;
    }

    setAssetSummaryError("");
    setAssetSummaryLoading(true);
    try {
      const response = await axios.post(
        API_ENDPOINT.get_asset_summary,
        { buyer_token: buyerToken },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setAssetSummary(response?.data?.response || response?.data || null);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.response ||
        error?.message ||
        "Failed to load asset summary";
      setAssetSummary(null);
      setAssetSummaryError(String(message));
    } finally {
      setAssetSummaryLoading(false);
    }
  }, [authToken, buyerToken, disableAssetSummaryFetch]);

  useEffect(() => {
    if (disableAssetSummaryFetch) {
      setAssetSummary(null);
      return;
    }

    if (!buyerToken || !authToken) return;

    const key = `${buyerToken}|${authToken}`;
    if (assetSummaryFetchKeyRef.current === key) return;
    assetSummaryFetchKeyRef.current = key;
    fetchAssetSummary();
  }, [authToken, buyerToken, disableAssetSummaryFetch, fetchAssetSummary]);

  const renderPills = (values = []) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
      {values.map((value, index) => (
        <span
          key={`${value}-${index}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "8px 12px",
            borderRadius: 999,
            background: "linear-gradient(135deg, rgba(37,99,235,0.08), rgba(14,165,233,0.08))",
            border: "1px solid rgba(37,99,235,0.12)",
            color: T.text,
            fontFamily: T.fontBody,
            fontSize: 14,
          }}
        >
          {formatDisplayValue(value)}
        </span>
      ))}
    </div>
  );

  const renderMetricGrid = (object, exclude = [], accent = T.brand) => {
    const entries = scalarEntries(object, exclude);
    if (!entries.length) {
      return (
        <div style={{ fontSize: 14, fontFamily: T.fontBody, color: T.textMuted }}>
          No scalar data in this section.
        </div>
      );
    }

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 14,
        }}
      >
        {entries.map(([key, value]) => (
          <MetricCard
            key={key}
            title={keyToLabel(key)}
            value={formatDisplayValue(value)}
            accent={accent}
            onClick={resolveCardNavigation(key)}
          />
        ))}
      </div>
    );
  };

  const renderNamedMetricCards = (items = [], { gridTemplateColumns = "repeat(auto-fit, minmax(210px, 1fr))" } = {}) => {
    const visibleItems = items.filter((item) => item && item.title !== undefined);
    if (!visibleItems.length) return null;

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns,
          gap: 14,
          marginBottom:10
        }}
      >
        {visibleItems.map((item) => (
          <MetricCard
            key={item.title}
            title={item.title}
            value={item.value}
            note={item.note}
            accent={item.accent || T.brand}
            tooltip={item.tooltip}
            onClick={item.onClick || resolveCardNavigation(`${item.title || ""} ${item.note || ""}`)}
          />
        ))}
      </div>
    );
  };

  const sumScalarValues = (object = {}) =>
    scalarEntries(object).reduce((sum, [, value]) => sum + normalizeMetricValue(value), 0);

  const renderArrayChart = ({
    title,
    rows,
    labelKey = "name",
    valueKey = "amount",
    accent = T.brand,
    limit = 8,
    chartHeight = 320,
    fullWidth = false,
    axisValueFormatter,
    tooltipValueFormatter,
    breakdownValueFormatter,
  }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return (
        <NestedCard style={fullWidth ? { gridColumn: "1 / -1" } : undefined}>
          <SubSectionHeader title={title} />
          <div style={{ fontSize: 14, fontFamily: T.fontBody, color: T.textMuted }}>
            No data provided in the current response.
          </div>
        </NestedCard>
      );
    }

    const chartRows = groupTopRows(rows, { labelKey, valueKey, limit });

    return (
      <NestedCard style={fullWidth ? { gridColumn: "1 / -1" } : undefined}>
        <SubSectionHeader title={title} />
        <div style={{ cursor: "default", minHeight: chartHeight + 32 }}>
          <HorizontalBarChart
            inline
            showHeading={false}
            chartHeight={chartHeight}
            valueSuffix=""
            axisValueFormatter={axisValueFormatter}
            tooltipValueFormatter={tooltipValueFormatter}
            chartData={[
              {
                label: title,
                backgroundColor: accent,
                data: chartRows.map((item) => item.__value),
              },
            ]}
            chartDataSet={{
              cardHeading: title,
              chartYAxisLabels: chartRows.map((item) => item.__label),
              fullLabels: chartRows.map((item) => item.__label),
              chartIndicators: [{ value: title, color: accent }],
              tooltipRows: chartRows,
              breakdownValueFormatter,
            }}
            loadingState={loadingState}
          />
        </div>
      </NestedCard>
    );
  };

  const renderLocationWisePrRaisedChart = (rows = []) => {
    const chartRows = Array.isArray(rows)
      ? rows
          .map((row) => ({
            location: normalizePrLocationLabel(row?.location),
            count: normalizeMetricValue(row?.count),
            value: normalizeLocationPrValue(row?.value),
          }))
          .filter((row) => row.location && (row.count > 0 || row.value > 0))
          .sort((a, b) => b.count - a.count)
      : [];

    if (!chartRows.length) {
      return (
        <NestedCard style={{ gridColumn: "1 / -1" }}>
          <SubSectionHeader title="Location wise PR raised" />
          <div style={{ fontSize: 14, fontFamily: T.fontBody, color: T.textMuted }}>
            No location wise PR raised data provided in the current response.
          </div>
        </NestedCard>
      );
    }

    const maxCount = Math.max(...chartRows.map((row) => row.count), 1);
    const totalCount = chartRows.reduce((sum, row) => sum + row.count, 0);
    const totalValue = chartRows.reduce((sum, row) => sum + row.value, 0);

    return (
      <NestedCard style={{ gridColumn: "1 / -1" }}>
        <SubSectionHeader title="Location wise PR raised" />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <MetricCard title="Total PR Raised" value={formatChartValue(totalCount)} note="Across locations" accent={T.brand} />
          <MetricCard title="Total PR Value" value={formatCurrencyCompact(totalValue)} note="Across locations" accent={T.success} />
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {chartRows.map((row) => {
            const width = Math.max(4, (row.count / maxCount) * 100);

            return (
              <div
                key={row.location}
                style={{
                  display: "grid",
                  gridTemplateColumns: isNarrowLayout ? "1fr" : "minmax(160px, 240px) minmax(0, 1fr) minmax(120px, auto)",
                  gap: 12,
                  alignItems: "center",
                  padding: "12px 14px",
                  borderRadius: 14,
                  background: "#F8FAFC",
                  border: `1px solid ${T.border}`,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 14, overflowWrap: "anywhere" }}>
                    {row.location}
                  </div>
                  <div style={{ color: T.textMuted, fontFamily: T.fontBody, fontSize: 12, marginTop: 3 }}>
                    Value {formatCurrencyCompact(row.value)}
                  </div>
                </div>

                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      height: 12,
                      borderRadius: 999,
                      background: "#E2E8F0",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${width}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${T.brand}, ${T.info})`,
                      }}
                    />
                  </div>
                </div>

                <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 14, textAlign: isNarrowLayout ? "left" : "right" }}>
                  {formatChartValue(row.count)} PRs
                </div>
              </div>
            );
          })}
        </div>
      </NestedCard>
    );
  };

  const renderTrendPanel = ({
    title,
    monthlyRows = {},
    yearlyRows = {},
    accent = T.brand,
    year,
    month,
    onYearChange,
    onMonthChange,
    yAxisValueFormatter,
    tooltipValueFormatter,
  }) => {
    const monthlyChart = filterTrendByPeriod(monthlyRows, { year, month });
    const yearlyChart = objectToSeriesChart(yearlyRows);
    const yearOptions = ["all", ...getTrendYears(monthlyRows)];
    const controlColumnStyle = isNarrowLayout
      ? { width: "100%", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }
      : { display: "flex", gap: 8, flexWrap: "wrap" };
    const selectStyle = {
      fontFamily: T.fontBody,
      fontSize: 14,
      borderRadius: 10,
      border: `1px solid ${T.border}`,
      padding: "8px 10px",
      background: "#fff",
      color: T.text,
      width: isNarrowLayout ? "100%" : "auto",
      minWidth: 0,
    };

    return (
      <NestedCard style={{ minWidth: 0 }}>
        <div style={getChartHeaderRowStyle(isNarrowLayout)}>
          <div style={chartHeaderTitleStyle}>{title}</div>
          <div style={controlColumnStyle}>
            <select
              value={year}
              onChange={(e) => onYearChange?.(e.target.value)}
              style={selectStyle}
            >
              {yearOptions.map((option) => (
                <option key={option} value={option}>
                  {option === "all" ? "All Years" : option}
                </option>
              ))}
            </select>
            <select
              value={month}
              onChange={(e) => onMonthChange?.(e.target.value)}
              style={selectStyle}
            >
              {MONTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: "grid", gap: 18 }}>
          <div style={{ minHeight: isNarrowLayout ? 280 : 320 }}>
            <LineChart
              inline
              showHeading={false}
              dataLabel={monthlyChart.labels}
              dataValues={monthlyChart.values}
              yAxisValueFormatter={yAxisValueFormatter}
              tooltipValueFormatter={tooltipValueFormatter}
              chartDataSet={{
                cardHeading: `${title} - Monthly`,
                chartLabels: [title],
                chartIndicators: [{ value: "Monthly", color: accent }],
                barColor: accent,
              }}
              loadingState={loadingState}
              disableDuration={true}
              chartHeight={isNarrowLayout ? 210 : 240}
            />
          </div>
          <div style={{ minHeight: isNarrowLayout ? 280 : 320 }}>
            <LineChart
              inline
              showHeading={false}
              dataLabel={yearlyChart.labels}
              dataValues={yearlyChart.values}
              yAxisValueFormatter={yAxisValueFormatter}
              tooltipValueFormatter={tooltipValueFormatter}
              chartDataSet={{
                cardHeading: `${title} - Yearly`,
                chartLabels: [title],
                chartIndicators: [{ value: "Yearly", color: accent }],
                barColor: accent,
              }}
              loadingState={loadingState}
              disableDuration={true}
              chartHeight={isNarrowLayout ? 210 : 240}
            />
          </div>
        </div>
      </NestedCard>
    );
  };

  const renderTrendChart = ({ title, rows, accent = T.brand, chartHeight = 280, yAxisValueFormatter, tooltipValueFormatter }) => {
    const chart = mapArrayToChart(rows, "name", "amount");
    return (
      <NestedCard>
        <SubSectionHeader title={title} />
        <LineChart
          inline
          showHeading={false}
          dataLabel={chart.labels}
          dataValues={chart.values}
          chartHeight={chartHeight}
          yAxisValueFormatter={yAxisValueFormatter}
          tooltipValueFormatter={tooltipValueFormatter}
          chartDataSet={{
            cardHeading: title,
            chartLabels: [title],
            chartIndicators: [{ value: title, color: accent }],
            barColor: accent,
          }}
          loadingState={loadingState}
          disableDuration={true}
        />
      </NestedCard>
    );
  };

  const renderTable = ({ title, rows, pagination }) => {
    const displayRows = Array.isArray(pagination?.rows) ? pagination.rows : rows;

    if (!Array.isArray(displayRows) || displayRows.length === 0) {
      return (
        <NestedCard style={{ padding: 24 }}>
          <SubSectionHeader title={title} />
          <div style={{ fontSize: 14, fontFamily: T.fontBody, color: T.textMuted }}>
            No data available.
          </div>
        </NestedCard>
      );
    }

    const columns = Array.from(
      rows.reduce((set, row) => {
        Object.keys(row || {}).forEach((key) => set.add(key));
        return set;
      }, new Set())
    );

    return (
      <NestedCard style={{ padding: 24 }}>
        <SubSectionHeader title={title} />
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: "0 10px",
              fontFamily: T.fontBody,
              fontSize: 14,
            }}
          >
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column}
                    style={{
                      textAlign: "left",
                      padding: "12px 16px",
                      color: T.textMuted,
                      fontFamily: T.fontDisplay,
                      whiteSpace: "nowrap",
                      fontSize: 13,
                    }}
                  >
                    {keyToLabel(column)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, index) => (
                <tr key={`${title}-${index}`}>
                  {columns.map((column, columnIndex) => (
                    <td
                      key={column}
                      style={{
                        padding: "14px 16px",
                        background: "#FFFFFF",
                        borderTop: "1px solid rgba(226,232,240,0.65)",
                        borderBottom: "1px solid rgba(226,232,240,0.65)",
                        borderLeft: columnIndex === 0 ? "1px solid rgba(226,232,240,0.65)" : "none",
                        borderRight:
                          columnIndex === columns.length - 1 ? "1px solid rgba(226,232,240,0.65)" : "none",
                        borderRadius:
                          columnIndex === 0
                            ? "16px 0 0 16px"
                            : columnIndex === columns.length - 1
                            ? "0 16px 16px 0"
                            : 0,
                        color: T.text,
                        verticalAlign: "middle",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {formatDisplayValue(row?.[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination ? (
          <div style={{ marginTop: 12 }}>
            <PaginationComponent
              pageNumber={pagination.pageNumber}
              currentPage={pagination.pageNumber || 0}
              itemsPerPage={pagination.itemsPerPage}
              totalItems={pagination.totalItems}
              setPageNumber={pagination.setPageNumber}
              previouPageClick={pagination.previouPageClick}
              nextPageClick={pagination.nextPageClick}
              disablePrevious={pagination.disablePrevious}
              disableNext={pagination.disableNext}
            />
          </div>
        ) : null}
      </NestedCard>
    );
  };

  const renderModuleCard = (moduleName, moduleValue, accent, displayName = moduleName) => {
    const departments = Array.isArray(moduleValue?.["User Department"]) ? moduleValue["User Department"] : [];
    const scalarValues = scalarEntries(moduleValue, ["User Department"]).filter(
      ([key]) => normalizeLookupKey(key) !== "pending",
    );
    const departmentChart = mapArrayToChart(departments, "department", "count", 5);
    const departmentData = departmentChart.labels.map((label, index) => ({
      label,
      value: departmentChart.values[index],
    }));
    const metricOrder = ["number", "open", "closed", "pending", "cancelled", "canceled", "ageing", "value", "vendor"];
    const orderedScalarValues = scalarValues.slice().sort(([leftKey], [rightKey]) => {
      const leftIndex = metricOrder.indexOf(normalizeLookupKey(leftKey));
      const rightIndex = metricOrder.indexOf(normalizeLookupKey(rightKey));
      const safeLeftIndex = leftIndex === -1 ? metricOrder.length : leftIndex;
      const safeRightIndex = rightIndex === -1 ? metricOrder.length : rightIndex;
      return safeLeftIndex - safeRightIndex;
    });
    const normalizedModuleName = normalizeLookupKey(moduleName);
    const moduleLabel =
      normalizedModuleName === "invoicevalidation"
        ? "Invoice"
        : normalizedModuleName === "grn"
        ? "GRN"
        : normalizedModuleName === "3waymatching"
        ? "P2P"
        : moduleName;
    const metricThemes = {
      number: { label: `Total ${moduleLabel}`, color: "#2563EB", background: "rgba(37,99,235,0.06)", emphasis: true },
      open: { label: `Open ${moduleLabel}`, color: "#0EA5E9", background: "rgba(14,165,233,0.06)" },
      closed: { label: `Closed ${moduleLabel}`, color: "#16A34A", background: "rgba(22,163,74,0.06)" },
      pending: { label: `Awaiting ${moduleLabel}`, color: "#F59E0B", background: "rgba(245,158,11,0.08)" },
      cancelled: { label: `Cancelled ${moduleLabel}`, color: "#EF4444", background: "rgba(239,68,68,0.06)" },
      canceled: { label: `Cancelled ${moduleLabel}`, color: "#EF4444", background: "rgba(239,68,68,0.06)" },
      ageing: {
        label: `${moduleLabel} Ageing`,
        color: "#7C3AED",
        background: "rgba(124,58,237,0.06)",
        note: "Average ageing in days",
      },
      value: {
        label: `${moduleLabel} Value`,
        color: "#0F766E",
        background: "rgba(15,118,110,0.06)",
        formatValue: (value) => `\u20B9${formatDisplayValue(value)}`,
        valueStyle: {
          fontSize: "clamp(17px, 0.95vw, 20px)",
          fontFamily: T.fontDisplay,
          whiteSpace: "nowrap",
          wordBreak: "normal",
          overflowWrap: "normal",
        },
      },
      vendor: { label: "Supplier Count", color: "#DB2777", background: "rgba(219,39,119,0.06)" },
    };
    const getMetricTheme = (key) => metricThemes[normalizeLookupKey(key)] || { label: keyToLabel(key), color: accent, background: "rgba(248,250,252,0.9)" };

    return (
      <NestedCard key={moduleName}>
        <SubSectionHeader title={displayName} />
        {scalarValues.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))",
              gap: 14,
              alignItems: "stretch",
            }}
          >
            {orderedScalarValues.map(([key, value]) => {
              const theme = getMetricTheme(key);

              return (
                <MetricCard
                  key={`${moduleName}-${key}`}
                  title={theme.label}
                  value={theme.formatValue ? theme.formatValue(value) : formatDisplayValue(value)}
                  note={theme.note}
                  accent={theme.color}
                  valueColor={theme.color}
                  valueStyle={theme.valueStyle}
                  emphasis={theme.emphasis}
                  cardStyle={{
                    gridColumn: theme.emphasis ? "1 / -1" : undefined,
                    background: `linear-gradient(180deg, ${theme.background} 0%, #FFFFFF 72%)`,
                    borderColor: hexToRgba(theme.color, theme.emphasis ? 0.28 : 0.16),
                  }}
                />
              );
            })}
          </div>
        )}
        {departments.length > 0 && (
          <>
            <Divider />
            <SubSectionHeader title="Requesting Department" />
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={departmentData} margin={{ top: 10, right: 12, left: 6, bottom: 72 }}>
                  <CartesianGrid stroke={T.border} strokeDasharray="3 4" vertical={false} />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    height={72}
                    tickMargin={12}
                    tick={(props) => <RechartsWrappedTick {...props} width={150} dy={12} />}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: T.textMuted, fontFamily: T.fontBody, fontSize: 14 }}
                    tickFormatter={(value) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value)}
                  />
                  <RechartsTooltip content={(props) => <CustomChartTooltip {...props} title="Requesting Department" />} />
                  <Bar dataKey="value" radius={[12, 12, 0, 0]} barSize={28} fill={accent} isAnimationActive animationDuration={400} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </NestedCard>
    );
  };

  const buildProcurementPipelineRows = (modules = {}) => {
    const rows = [
      { key: "PR", title: "Purchase Requisitions", accent: T.brand },
      { key: "RFQ", title: "RFQ", accent: T.info },
      { key: "Quotation", title: "Quotation", accent: T.success },
      { key: "Comparison", title: "Comparison", accent: T.warning },
      { key: "PO", title: "Purchase Orders", accent: T.purple },
      { key: "Rate Contract", title: "Rate Contract", accent: "#22C55E" },
      { key: "Invoice Validation", title: "Invoice Validation", accent: "#0EA5E9" },
      { key: "GRN", title: "GRN", accent: "#14B8A6" },
      { key: "Asset Register", title: "Asset Register", accent: "#FB923C" },
      { key: "3-Way Matching", title: "3 Way Matching", accent: "#8B5CF6" },
    ];

    return rows.map((row) => {
      const moduleValue = modules?.[row.key] || {};
      const total = normalizeMetricValue(moduleValue?.Number);
      const value = moduleValue?.Value ?? 0;
      const vendor = normalizeMetricValue(moduleValue?.Vendor);
      const open = normalizeMetricValue(moduleValue?.Open);
      const closed = normalizeMetricValue(moduleValue?.Closed);
      const pending = normalizeMetricValue(moduleValue?.Pending);
      const cancelled = normalizeMetricValue(moduleValue?.Cancelled);
      const approved = normalizeMetricValue(moduleValue?.Approved);
      const rejected = normalizeMetricValue(moduleValue?.Rejected);
      const ageing = moduleValue?.Ageing ?? 0;
      const cycleDays = moduleValue?.["Cycle Time"] ?? 0;
      const progress = total > 0 ? Math.min(100, (closed / total) * 100) : 0;
      const approvalRate = approved + rejected > 0 ? (approved / (approved + rejected)) * 100 : 0;

      return {
        ...row,
        total,
        value,
        vendor,
        open,
        closed,
        pending,
        cancelled,
        approved,
        rejected,
        ageing,
        cycleDays,
        progress,
        approvalRate,
      };
    });
  };

  const renderProgressBar = (value, accent) => (
    <div style={{ minWidth: 160 }}>
      <div
        style={{
          width: "100%",
          height: 8,
          borderRadius: 999,
          background: "#E8EEF8",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, value))}%`,
            height: "100%",
            borderRadius: 999,
            background: `linear-gradient(90deg, ${accent} 0%, ${accent}cc 100%)`,
          }}
        />
      </div>
      <div style={{ marginTop: 6, fontSize: 14, fontFamily: T.fontBody, color: T.textMuted }}>
        {value.toFixed(0)}% done
      </div>
    </div>
  );

  const renderProcurementPipelineTable = (modules = {}) => {
    const rows = buildProcurementPipelineRows(modules);
    const columns = ["Module", "Total", "Value", "Open", "Closed", "Awaiting", "Cancelled", "Supplier", "Ageing", "Cycle Days", "Progress"];
    const cellBase = {
      padding: "14px 14px",
      background: "#FFFFFF",
      borderTop: "1px solid rgba(226,232,240,0.72)",
      borderBottom: "1px solid rgba(226,232,240,0.72)",
      verticalAlign: "middle",
    };
    const statusPill = (value, color, background) => (
      <span
        style={{
          display: "inline-flex",
          minWidth: 54,
          justifyContent: "center",
          padding: "5px 10px",
          borderRadius: 999,
          background,
          color,
          fontFamily: T.fontDisplay,
          lineHeight: 1.2,
        }}
      >
        {value.toLocaleString("en-IN")}
      </span>
    );

    return (
      <NestedCard style={{ background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)" }}>
        <SubSectionHeader title="Request & Order Status Monitor" />
        <div style={{ overflowX: "auto", paddingBottom: 2 }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: "0 10px",
              fontFamily: T.fontBody,
              fontSize: 14,
            }}
          >
            <thead>
              <tr>
                {columns.map((heading) => (
                  <th
                    key={heading}
                    style={{
                      textAlign: "left",
                      padding: "10px 14px",
                      color: T.textMuted,
                      fontFamily: T.fontDisplay,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      fontSize: 11,
                      background: "transparent",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td
                    style={{
                      ...cellBase,
                      borderLeft: `4px solid ${row.accent}`,
                      borderTopLeftRadius: 14,
                      borderBottomLeftRadius: 14,
                      color: T.text,
                      fontFamily: T.fontDisplay,
                      minWidth: 210,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: row.accent,
                          boxShadow: `0 0 0 4px ${hexToRgba(row.accent, 0.12)}`,
                          flexShrink: 0,
                        }}
                      />
                      <span>{row.title}</span>
                    </div>
                  </td>
                  <td style={{ ...cellBase, color: row.accent, fontFamily: T.fontDisplay }}>{row.total.toLocaleString("en-IN")}</td>
                  <td style={{ ...cellBase, color: T.textSec, fontFamily: T.fontDisplay, whiteSpace: "nowrap" }}>
                    {formatCurrencyRoundedCr(row.value)}
                  </td>
                  <td style={cellBase}>
                    {statusPill(row.open, row.accent, hexToRgba(row.accent, 0.1))}
                  </td>
                  <td style={cellBase}>
                    {statusPill(row.closed, "#16A34A", "rgba(22,163,74,0.1)")}
                  </td>
                  <td style={cellBase}>
                    {statusPill(row.pending, "#F59E0B", "rgba(245,158,11,0.12)")}
                  </td>
                  <td style={cellBase}>
                    {statusPill(row.cancelled, "#EF4444", "rgba(239,68,68,0.1)")}
                  </td>
                  <td style={{ ...cellBase, color: T.textSec, fontFamily: T.fontDisplay }}>{row.vendor.toLocaleString("en-IN")}</td>
                  <td style={{ ...cellBase, color: T.textSec, fontFamily: T.fontDisplay }}>{formatDisplayValue(row.ageing)}</td>
                  <td style={{ ...cellBase, color: T.textSec, fontFamily: T.fontDisplay }}>{formatDisplayValue(row.cycleDays)}</td>
                  <td
                    style={{
                      ...cellBase,
                      borderRight: "1px solid rgba(226,232,240,0.72)",
                      borderTopRightRadius: 14,
                      borderBottomRightRadius: 14,
                      minWidth: 190,
                    }}
                  >
                    {renderProgressBar(row.progress, row.accent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </NestedCard>
    );
  };

  const renderSpendItemSection = () => {
    if (!spendItems?.length) return null;

    return (
      <SectionCard title="Spend Item Details">
        <div style={{ marginBottom: 12 }}>
          <SearchBar
            onChange={(q) => {
              setSpendSearchQuery?.(q);
              setSpendPageNumber?.(1);
            }}
          />
        </div>
        <DataTable
          headings={spendTableHeadings}
          tableData={spendItems}
          actions={false}
          tableSize={spendItems?.length === 0 ? "100%" : "100vw"}
          viewBtn={false}
          deleteBtn={false}
          editBtn={false}
          sorting={true}
          errorMessage=""
          isLoading={spendTableLoading}
          itemsTable={true}
          setSortingColumnName={setSpendSortingColumnName}
          sortingColumnName={spendSortingColumnName}
          setSortingOrder={setSpendSortingOrder}
          sortingOrder={spendSortingOrder}
        />
        <div style={{ marginTop: 12 }}>
          <PaginationComponent
            pageNumber={spendPageNumber}
            currentPage={spendPageNumber || 0}
            itemsPerPage={spendPaginationData?.current_page_count || 0}
            totalItems={spendPaginationData?.page_count || 0}
            setPageNumber={setSpendPageNumber}
            previouPageClick={() => previouPageClick(spendPageNumber, setSpendPageNumber)}
            nextPageClick={() => nextPageClick(spendPageNumber, setSpendPageNumber, spendPaginationData)}
            disablePrevious={parseInt(spendPageNumber) === 1 || spendPageNumber === ""}
            disableNext={parseInt(spendPageNumber) === spendPaginationData?.page_count || !spendPaginationData || spendPageNumber === ""}
          />
        </div>
      </SectionCard>
    );
  };

  const renderExecutiveSummary = () => {
    const pipelineRows = buildProcurementPipelineRows(sections.dashboard1?.Module || {});
    const poModule = sections.dashboard1?.Module?.PO || {};
    const totalSpend = normalizeMetricValue(sections.dashboard2?.["Budget vs Actual Spend"]?.["Actual Spend"]);
    const totalSavings = normalizeMetricValue(sections.dashboard3?.["Total Savings Achieved"]);
    const savingsPercent = totalSpend > 0 ? (totalSavings / totalSpend) * 100 : 0;
    const activeVendors = Array.isArray(sections.dashboard2?.["Spend by Vendor"])
      ? sections.dashboard2["Spend by Vendor"].length
      : Array.isArray(sections.dashboard4?.["Vendor Win Ratio"])
        ? sections.dashboard4["Vendor Win Ratio"].length
        : 0;
    const procurementCycleRows = pipelineRows.map((row) => normalizeMetricValue(row.cycleDays)).filter((value) => value > 0);
    const procurementCycleTime = procurementCycleRows.length
      ? procurementCycleRows.reduce((sum, value) => sum + value, 0) / procurementCycleRows.length
      : 0;
    const automationKpis = sections.dashboard6?.KPIs || {};
    const automationRateRaw =
      automationKpis?.["Automation Rate"] ??
      automationKpis?.["Automation Success Rate"] ??
      automationKpis?.["Automation Success %"] ??
      automationKpis?.["Automation Percentage"];
    const touchlessTotal = normalizeMetricValue(sections.dashboard6?.["Total Touchless and Autonomous"]);
    const manualTotal = normalizeMetricValue(sections.dashboard6?.["Total Manual"]);
    const automationRate =
      normalizeMetricValue(automationRateRaw) || (touchlessTotal + manualTotal > 0 ? (touchlessTotal / (touchlessTotal + manualTotal)) * 100 : 0);
    const poYearSpendRows = Object.entries(poModule?.["Year Wise PO Spend"] || {})
      .map(([year, value]) => ({
        year,
        amount: normalizeMetricValue(value),
      }))
      .sort((a, b) => Number(b.year) - Number(a.year));
    const spendTooltip = (
      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontFamily: T.fontDisplay, color: T.textMuted, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            Total Addressable Spend
          </div>
          <div style={{ marginTop: 2, fontSize: 15, fontFamily: T.fontDisplay, color: T.text }}>
            {formatCurrencyCompact(totalSpend)} total
          </div>
          <div style={{ marginTop: 1, fontSize: 12, color: T.textMuted }}>PO Year Wise Spend</div>
        </div>
        <div style={{ display: "grid", gap: 6, paddingRight: 4 }}>
          {poYearSpendRows.length > 0 ? (
            poYearSpendRows.map((row) => (
              <div
                key={row.year}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "6px 8px",
                  borderRadius: 10,
                  background: "rgba(248,250,252,0.9)",
                }}
              >
                <span style={{ color: T.textSec, fontSize: 13, fontFamily: T.fontDisplay }}>{row.year}</span>
                <span style={{ color: T.text, fontSize: 13, fontFamily: T.fontDisplay }}>{formatCurrencyCompact(row.amount)}</span>
              </div>
            ))
          ) : (
            <div style={{ color: T.textMuted, fontSize: 13 }}>No yearly PO spend data available.</div>
          )}
        </div>
      </div>
    );

    return (
      <NestedCard>
        <SubSectionHeader title="Procurement Command Center" />
        <div style={{ marginBottom: 12, color: T.textMuted, fontSize: 14, fontFamily: T.fontBody }}>
          One-glance procurement health check pulled from spend, savings, supplier, cycle-time, and automation analytics.
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 14,
          }}
        >
          <MetricCard title="Total Addressable Spend" value={formatCurrencyCompact(totalSpend)} note="Actual spend" accent={T.brand} tooltip={spendTooltip} onClick={resolveCardNavigation("PO Total Addressable Spend")} />
          <MetricCard title="Total Savings" value={formatCurrencyCompact(totalSavings)} note="Achieved savings" accent={T.success} onClick={resolveCardNavigation("RFQ savings")} />
          <MetricCard title="Savings %" value={formatPercentCompact(savingsPercent)} note="Savings as a share of spend" accent={T.success} onClick={resolveCardNavigation("RFQ savings")} />
          <MetricCard title="Active Suppliers" value={String(activeVendors)} note="Supplier activity in spend analytics" accent={T.purple} onClick={resolveCardNavigation("Suppliers vendors")} />
          <MetricCard title="Procurement Cycle Time" value={`${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 }).format(procurementCycleTime)} days`} note="Avg. across active modules" accent={T.warning} />
          <MetricCard title="Automation Rate" value={formatPercentCompact(automationRate)} note="Touchless share of automation" accent={T.info} onClick={resolveCardNavigation("3 Way Matching automation")} />
        </div>
      </NestedCard>
    );
  };

  const renderApprovalAnalytics = (modules = {}) => {
    const rows = buildProcurementPipelineRows(modules);
    const manualBreakdown = sections.dashboard6?.["Manual Interventions Breakdown"] || {};
    const pendingPrApprovals = normalizeMetricValue(manualBreakdown?.["Awaiting PR"]);
    const pendingPoApprovals = normalizeMetricValue(manualBreakdown?.["Awaiting PO Approvals"]);
    const pendingThreeWay = normalizeMetricValue(manualBreakdown?.["Awaiting 3-Way Matching"]);
    const blockedThreeWay = normalizeMetricValue(manualBreakdown?.["Blocked 3-Way Matching"]);
    const totalPendingApprovals = pendingPrApprovals + pendingPoApprovals + pendingThreeWay + blockedThreeWay;
    const totalApproved = rows.reduce((sum, row) => sum + normalizeMetricValue(row.approved), 0);
    const totalRejected = rows.reduce((sum, row) => sum + normalizeMetricValue(row.rejected), 0);
    const avgApprovalTime = rows.length
      ? rows.reduce((sum, row) => sum + normalizeMetricValue(row.cycleDays), 0) / rows.length
      : 0;
    const bottleneckRow = rows.reduce((best, row) => {
      const bestScore = normalizeMetricValue(best?.pending) + normalizeMetricValue(best?.ageing);
      const rowScore = normalizeMetricValue(row.pending) + normalizeMetricValue(row.ageing);
      return rowScore > bestScore ? row : best;
    }, rows[0] || null);

    return (
      <NestedCard>
        <SubSectionHeader title="Approval Workflows" />
        <div style={{ marginBottom: 12, color: T.textMuted, fontSize: 14, fontFamily: T.fontBody }}>
          Awaiting approvals and cycle-time bottlenecks derived from the active procurement modules.
        </div>
        {renderNamedMetricCards(
          [
            {
              title: "Awaiting Approvals",
              value: String(totalPendingApprovals),
              note: "Awaiting PR, PO and 3-way items",
              accent: T.warning,
            },
            {
              title: "Average Approval Cycle Time",
              value: `${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 }).format(avgApprovalTime)} days`,
              note: "Average cycle time across modules",
              accent: T.brand,
            },
            {
              title: "Approved",
              value: String(totalApproved),
              accent: T.success,
            },
            {
              title: "Rejected",
              value: String(totalRejected),
              accent: T.danger,
            },
            {
              title: "Approval Bottleneck",
              value: bottleneckRow?.title || "-",
              note: bottleneckRow ? `${formatDisplayValue(bottleneckRow.pending)} pending` : "No bottleneck data",
              accent: T.purple,
            },
            {
              title: "Overall Approval Rate",
              value: formatPercentCompact(
                totalApproved + totalRejected > 0 ? (totalApproved / (totalApproved + totalRejected)) * 100 : 0
              ),
              accent: T.info,
            },
          ],
          { gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" }
        )}
      </NestedCard>
    );
  };

  const renderDashboard1 = () => {
    const data = sections.dashboard1 || {};
    const modules = data?.Module || {};
    const prModule = modules?.PR || {};
    const poModule = modules?.PO || {};
    const totalSpend = normalizeMetricValue(data?.["Budget vs Actual Spend"]?.["Actual Spend"] ?? poModule?.Value);
    const pipelineRows = buildProcurementPipelineRows(modules);
    const totalWorkflow = pipelineRows.reduce((sum, row) => sum + normalizeMetricValue(row.total), 0);
    const totalOpen = pipelineRows.reduce((sum, row) => sum + normalizeMetricValue(row.open), 0);
    const totalClosed = pipelineRows.reduce((sum, row) => sum + normalizeMetricValue(row.closed), 0);
    const totalPending = pipelineRows.reduce((sum, row) => sum + normalizeMetricValue(row.pending), 0);
    const totalCancelled = pipelineRows.reduce((sum, row) => sum + normalizeMetricValue(row.cancelled), 0);
    const averageAgeing = pipelineRows.length
      ? pipelineRows.reduce((sum, row) => sum + normalizeMetricValue(row.ageing), 0) / pipelineRows.length
      : 0;
    const activeModules = pipelineRows.filter((row) => row.total > 0).length;
    const poYearSpendRows = Object.entries(poModule?.["Year Wise PO Spend"] || {})
      .map(([year, value]) => ({
        year,
        amount: normalizeMetricValue(value),
      }))
      .sort((a, b) => Number(b.year) - Number(a.year));
    const renderTooltipBreakdown = (metricLabel, totalValue, selector) => (
      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontFamily: T.fontDisplay, color: T.textMuted, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            {metricLabel}
          </div>
          <div style={{ marginTop: 2, fontSize: 15, fontFamily: T.fontDisplay, color: T.text }}>
            {formatDisplayValue(totalValue)} total
          </div>
          <div style={{ marginTop: 1, fontSize: 12, color: T.textMuted }}>Module Breakdown</div>
        </div>
        <div
          style={{
            display: "grid",
            gap: 6,
            paddingRight: 4,
          }}
        >
          {pipelineRows.map((row) => {
            const rawValue = selector(row);
            return (
              <div
                key={`${metricLabel}-${row.key}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "6px 8px",
                  borderRadius: 10,
                  background: "rgba(248,250,252,0.9)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: row.accent,
                      flex: "0 0 auto",
                    }}
                  />
                  <span style={{ color: T.textSec, fontSize: 13, fontFamily: T.fontDisplay, whiteSpace: "nowrap" }}>{row.title}</span>
                </div>
                <span style={{ color: T.text, fontSize: 13, fontFamily: T.fontDisplay, whiteSpace: "nowrap" }}>{formatDisplayValue(rawValue)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
    const spendTooltip = (
      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontFamily: T.fontDisplay, color: T.textMuted, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            Total Addressable Spend
          </div>
          <div style={{ marginTop: 2, fontSize: 15, fontFamily: T.fontDisplay, color: T.text }}>
            {formatCurrencyCompact(totalSpend)} total
          </div>
          <div style={{ marginTop: 1, fontSize: 12, color: T.textMuted }}>Year Wise PO Spend</div>
        </div>
        <div style={{ display: "grid", gap: 6, paddingRight: 4 }}>
          {poYearSpendRows.length > 0 ? (
            poYearSpendRows.map((row) => (
              <div
                key={row.year}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "6px 8px",
                  borderRadius: 10,
                  background: "rgba(248,250,252,0.9)",
                }}
              >
                <span style={{ color: T.textSec, fontSize: 13, fontFamily: T.fontDisplay }}>{row.year}</span>
                <span style={{ color: T.text, fontSize: 13, fontFamily: T.fontDisplay }}>{formatCurrencyCompact(row.amount)}</span>
              </div>
            ))
          ) : (
            <div style={{ color: T.textMuted, fontSize: 13 }}>No yearly spend data available.</div>
          )}
        </div>
      </div>
    );
    const workflowTooltip = renderTooltipBreakdown("Total Workflow Volume", totalWorkflow, (row) => row.total);
    const openTooltip = renderTooltipBreakdown("Open Work Items", totalOpen, (row) => row.open);
    const closedTooltip = renderTooltipBreakdown("Closed Work Items", totalClosed, (row) => row.closed);
    const pendingTooltip = renderTooltipBreakdown("Pending Work Items", totalPending, (row) => row.pending);
    const cancelledTooltip = renderTooltipBreakdown("Cancelled Work Items", totalCancelled, (row) => row.cancelled);

    return (
      <SectionCard title="Procurement Operations Control Tower">
        <div style={{ marginBottom: 16, color: T.textMuted, fontSize: 14, fontFamily: T.fontBody }}>
          Real-time procurement movement across PR, RFQ, PO, invoice and matching workflows.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
            marginBottom: 18,
          }}
        >
          <MetricCard title="Total Transaction" value={totalWorkflow.toLocaleString("en-IN")} note="Across all modules" accent={T.brand} tooltip={workflowTooltip} onClick={resolveCardNavigation("PO PR RFQ invoice matching transactions")} />
          <MetricCard title="Open Transaction" value={totalOpen.toLocaleString("en-IN")} note="Across all modules" accent={T.info} tooltip={openTooltip} onClick={resolveCardNavigation("PO open transactions")} />
          <MetricCard title="Closed Transaction" value={totalClosed.toLocaleString("en-IN")} note="Across all modules" accent={T.success} tooltip={closedTooltip} onClick={resolveCardNavigation("PO closed transactions")} />
          <MetricCard title="Awaiting Transaction" value={totalPending.toLocaleString("en-IN")} note="Across all modules" accent={T.warning} tooltip={pendingTooltip} onClick={resolveCardNavigation("PR awaiting approvals")} />
          <MetricCard title="Cancelled Work" value={totalCancelled.toLocaleString("en-IN")} note="Across all modules" accent={T.danger} tooltip={cancelledTooltip} onClick={resolveCardNavigation("PR cancelled")} />
          <MetricCard title="Average Ageing" value={averageAgeing.toFixed(2)} note="Days across modules" accent={T.purple} />
          <MetricCard title="Total Addressable Spend" value={formatCurrencyCompact(totalSpend)} note="Actual spend" accent="#14B8A6" tooltip={spendTooltip} onClick={resolveCardNavigation("PO addressable spend")} />
          <MetricCard title="Active Modules" value={String(activeModules)} note="Modules with data" accent="#14B8A6" />
        </div>

        <div style={{ marginBottom: 16 }}>
          {renderProcurementPipelineTable(modules)}
        </div>

        <div style={{ marginBottom: 16 }}>
          <SubSectionHeader title="Exception Watch" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
            {renderModuleCard("Invoice Validation", modules?.["Invoice Validation"] || {}, "#0EA5E9")}
            {renderModuleCard("GRN", modules?.GRN || {}, "#14B8A6")}
            {renderModuleCard("3-Way Matching", modules?.["3-Way Matching"] || {}, "#8B5CF6", "3 Way Matching")}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          {renderApprovalAnalytics(modules)}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompactLayout ? "repeat(auto-fit, minmax(280px, 1fr))" : "repeat(auto-fit, minmax(340px, 1fr))",
            gap: 16,
            marginBottom: 16,
          }}
        >
          {renderTrendPanel({
            title: "Purchase Requisitions Trend",
            monthlyRows: prModule?.["Monthly PRs Created"],
            yearlyRows: prModule?.["Yearly PRs Created"],
            accent: T.brand,
            year: prTrendYear,
            month: prTrendMonth,
            onYearChange: setPrTrendYear,
            onMonthChange: setPrTrendMonth,
          })}
          {renderTrendPanel({
            title: "RFQ Trend",
            monthlyRows: sections.dashboard4?.["Monthly RFQs Created"] || {},
            yearlyRows: sections.dashboard4?.["Yearly RFQs Created"] || {},
            accent: T.info,
            year: rfqTrendYear,
            month: rfqTrendMonth,
            onYearChange: setRfqTrendYear,
            onMonthChange: setRfqTrendMonth,
          })}
          {renderTrendPanel({
            title: "Purchase Orders Trend",
            monthlyRows: poModule?.["Monthly POs Created"] || {},
            yearlyRows: poModule?.["Yearly POs Created"] || {},
            accent: T.success,
            year: poTrendYear,
            month: poTrendMonth,
            onYearChange: setPoTrendYear,
            onMonthChange: setPoTrendMonth,
          })}
        </div>
      </SectionCard>
    );
  };

  const renderDashboard2 = () => {
    const data = sections.dashboard2 || {};
    const annualSpendTrendRows = getNestedObjectValue(
      data,
      ["Annual Spend Trend"],
      ["Yearly Spend Trend"],
      ["Annual Spend"],
      ["Yearly Spend"],
      ["annual_spend_trend"],
      ["yearly_spend_trend"],
      ["yearly_spend"],
      ["yearlySpend"]
    );
    const repeatVsFirstTimeSpend =
      getNestedObjectValue(
        data,
        ["Repeat vs First-Time Spend"],
        ["Repeat vs First Time Spend"],
        ["Repeat vs First-Time Sourcing"]
      ) || {};
    const repeatSpend = getNestedObjectValue(
      repeatVsFirstTimeSpend,
      ["Repeat Spend"],
      ["Repeat Sourcing"],
      ["Repeat Purchase Spend"]
    );
    const firstTimeSpend = getNestedObjectValue(
      repeatVsFirstTimeSpend,
      ["First-Time Spend"],
      ["First Time Spend"],
      ["First Time Sourcing"]
    );
    const budgetData = sections.budget_savings_analytics || {};
    const budgetByYear = Array.isArray(budgetData?.["Budget by Year"])
      ? budgetData["Budget by Year"].map((row) => ({
          name: row?.name || "Unmapped",
          amount: normalizeMetricValue(row?.budget),
        }))
      : [];
    const budgetByMonth = Array.isArray(budgetData?.["Budget by Month"])
      ? budgetData["Budget by Month"].map((row) => ({
          name: row?.name || "Unmapped",
          amount: normalizeMetricValue(row?.budget),
        }))
      : [];
    const budgetByDepartment = Array.isArray(budgetData?.["Budget by Department"])
      ? budgetData["Budget by Department"].map((row) => ({
          name: row?.name || "Unmapped",
          amount: normalizeMetricValue(row?.budget),
        }))
      : [];
    const budgetByGlCode = Array.isArray(budgetData?.["Budget by GL Code"])
      ? budgetData["Budget by GL Code"].map((row) => ({
          name: row?.name || "Unmapped",
          amount: normalizeMetricValue(row?.budget),
        }))
      : [];
    const budgetByProjectCode = Array.isArray(budgetData?.["Budget by Project Code"])
      ? budgetData["Budget by Project Code"].map((row) => ({
          name: row?.name || "Unmapped",
          amount: normalizeMetricValue(row?.budget),
        }))
      : [];
    const budgetByCostCenter = Array.isArray(budgetData?.["Budget by Cost Center"])
      ? budgetData["Budget by Cost Center"].map((row) => ({
          name: row?.name || "Unmapped",
          amount: normalizeMetricValue(row?.budget),
        }))
      : [];
    const overallBudget = budgetData?.["Overall Budget"] ?? data?.["Budget vs Actual Spend"]?.Budget;
    const budgetYearTotal = budgetByYear.reduce((sum, row) => sum + row.amount, 0);

    return (
      <SectionCard title="Enterprise Spend Analytics">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 14,
          }}
        >
          <MetricCard title="PR Spend" value={formatCurrencyCompact(overallBudget)} note="Total PR value" accent={T.brand} onClick={resolveCardNavigation("PR budget")} />
          <MetricCard title="Overall Spend" value={formatCurrencyCompact(data?.["Budget vs Actual Spend"]?.["Actual Spend"])} note="PO value" accent={T.success} onClick={resolveCardNavigation("PO spend")} />
          <MetricCard title="Rate Contracted Spend" value={formatCurrencyCompact(data?.["Contracted vs Non-Contracted Spend"]?.["Contracted Spend"])} note="RC to PO spend" accent={T.warning} onClick={resolveCardNavigation("rate contract spend")} />
          <MetricCard title="PO Spend" value={formatCurrencyCompact(data?.["Contracted vs Non-Contracted Spend"]?.["Non-Contracted Spend"])} note="RFQ to PO spend" accent={T.danger} onClick={resolveCardNavigation("PO Spend")} />
          <MetricCard title="Repeat Sourcing" value={formatCurrencyCompact(repeatSpend)} accent={T.purple} onClick={resolveCardNavigation("RFQ sourcing")} />
          <MetricCard title="First Time Sourcing" value={formatCurrencyCompact(firstTimeSpend)} accent={T.info} onClick={resolveCardNavigation("RFQ sourcing")} />
          <MetricCard title="Maverick Spend" value={formatCurrencyCompact(data?.["Maverick Spend"])} accent={T.danger} onClick={resolveCardNavigation("PO maverick spend")} />
        </div>


        {/* <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 14,
            marginBottom: 16,
          }}
        >
          <MetricCard title="Total Spend" value={formatCurrencyCompact(data?.["Budget vs Actual Spend"]?.["Actual Spend"])} note="Latest actual spend" accent={T.brand} />
          <MetricCard title="Addressable Spend" value={formatCurrencyCompact(data?.["Contracted vs Non-Contracted Spend"]?.["Contracted Spend"])} note="Spend managed through contracts" accent={T.success} />
          <MetricCard title="Non-Addressable Spend" value={formatCurrencyCompact(data?.["Contracted vs Non-Contracted Spend"]?.["Non-Contracted Spend"])} note="Outside contracted coverage" accent={T.danger} />
          <MetricCard
            title="Spend Under Management"
            value={formatPercentCompact(
              normalizeMetricValue(data?.["Budget vs Actual Spend"]?.["Actual Spend"]) > 0
                ? (normalizeMetricValue(data?.["Contracted vs Non-Contracted Spend"]?.["Contracted Spend"]) /
                    normalizeMetricValue(data?.["Budget vs Actual Spend"]?.["Actual Spend"])) *
                    100
                : 0
            )}
            note="Contracted share of total spend"
            accent={T.warning}
          />
        </div> */}

        <Divider />



        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
            gap: 16,
            marginBottom: 16,
          }}
        >
          <NestedCard style={{ minHeight: 430 }}>
            <SubSectionHeader title="Spend by Year" />
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={budgetByYear}
                    dataKey="amount"
                    nameKey="name"
                    innerRadius={78}
                    outerRadius={112}
                    paddingAngle={4}
                    stroke="rgba(255,255,255,0.92)"
                    strokeWidth={2}
                    isAnimationActive
                    animationDuration={400}
                  >
                    {budgetByYear.map((entry, index) => (
                      <Cell
                        key={`budget-year-${index}`}
                        fill={[T.brand, T.success, T.info, T.warning, T.purple][index % 5]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip content={(props) => <CustomChartTooltip {...props} title="Spend by Year" showPercent total={budgetYearTotal} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <RechartsLegend rows={budgetByYear.map((row) => ({ label: row.name }))} colors={[T.brand, T.success, T.info, T.warning, T.purple]} />
          </NestedCard>

          <NestedCard style={{ minHeight: 430 }}>
            <SubSectionHeader title="Spend by Month" />
            <div style={{ minHeight: 320 }}>
              <LineChart
                inline
                showHeading={false}
                dataLabel={budgetByMonth.map((row) => row.name)}
                dataValues={budgetByMonth.map((row) => row.amount)}
                yAxisValueFormatter={formatCurrencyCompact}
                tooltipValueFormatter={formatCurrencyCompact}
                chartDataSet={{
                  cardHeading: "Spend by Month",
                  chartLabels: ["Spend by Month"],
                  chartIndicators: [{ value: "Spend", color: T.brand }],
                  barColor: T.brand,
                }}
                loadingState={loadingState}
                disableDuration={true}
                chartHeight={280}
              />
            </div>
          </NestedCard>

          {renderArrayChart({
            title: "Spend by Department",
            rows: budgetByDepartment,
            accent: T.info,
            limit: 8,
            chartHeight: 360,
            axisValueFormatter: formatCurrencyCompact,
            tooltipValueFormatter: formatCurrencyCompact,
          })}
          {renderArrayChart({
            title: "Spend by Project Code",
            rows: budgetByProjectCode,
            accent: T.success,
            limit: 8,
            chartHeight: 360,
            axisValueFormatter: formatCurrencyCompact,
            tooltipValueFormatter: formatCurrencyCompact,
          })}
          {renderArrayChart({
            title: "Spend by Cost Center",
            rows: budgetByCostCenter,
            accent: T.warning,
            limit: 8,
            chartHeight: 360,
            axisValueFormatter: formatCurrencyCompact,
            tooltipValueFormatter: formatCurrencyCompact,
          })}
          {renderArrayChart({
            title: "Spend by GL Code",
            rows: budgetByGlCode,
            accent: T.purple,
            limit: 10,
            chartHeight: 360,
            axisValueFormatter: formatCurrencyCompact,
            tooltipValueFormatter: formatCurrencyCompact,
          })}
        </div>

        <Divider />
        {renderLocationWisePrRaisedChart(sections.location_wise_pr_raised)}

        <Divider />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            gap: 16,
          }}
        >
          {renderArrayChart({ title: "Spend by Category", rows: data?.["Spend by Category"], accent: T.success, limit: 10, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact, breakdownValueFormatter: formatCurrencyInCrores })}
          {renderArrayChart({ title: "Spend by Department", rows: data?.["Spend by Department"], accent: T.brand, limit: 10, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact, breakdownValueFormatter: formatCurrencyInCrores })}
          {renderArrayChart({ title: "Spend by Supplier", rows: data?.["Spend by Vendor"], accent: T.purple, limit: 10, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact, breakdownValueFormatter: formatCurrencyInCrores })}
          {/* {renderArrayChart({ title: "Top 10 Vendors", rows: data?.["Top 10 Vendors"], accent: T.warning, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact })} */}
          {renderArrayChart({ title: "Spend by Location", rows: data?.["Spend by Location"], accent: T.info, limit: 10, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact, breakdownValueFormatter: formatCurrencyInCrores })}
          {renderArrayChart({ title: "OPEX vs CAPEX", rows: data?.["OPEX vs CAPEX"], accent: "#14B8A6", limit: 10, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact, breakdownValueFormatter: formatCurrencyInCrores })}
        </div>

        <Divider />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            gap: 16,
          }}
        >
          {renderTrendChart({ title: "Monthly Spend Trend", rows: data?.["Monthly Spend Trend"], accent: T.brand, yAxisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact })}
          {renderTrendChart({ title: "Annual Spend Trend", rows: annualSpendTrendRows, accent: T.success, yAxisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact })}
        </div>



        <Divider />
        {renderSpendItemSection()}
      </SectionCard>
    );
  };

  const renderDashboard3 = () => {
    const data = sections.dashboard3 || {};
    const savingsByDepartment = Array.isArray(data?.["Savings by Department"])
      ? data["Savings by Department"].map((row) => ({
          name: row?.name || "Unmapped",
          amount: normalizeMetricValue(row?.amount),
        }))
      : [];
    const totalSavingsAchieved = normalizeMetricValue(data?.["Total Savings Achieved"]);
    const savingsPercent = normalizeMetricValue(sections.dashboard2?.["Budget vs Actual Spend"]?.["Actual Spend"]) > 0
      ? (totalSavingsAchieved / normalizeMetricValue(sections.dashboard2?.["Budget vs Actual Spend"]?.["Actual Spend"])) * 100
      : 0;

    return (
      <SectionCard title="Savings & Avoided Cost Analytics">
        <div style={{ marginBottom: 16, color: T.textMuted, fontSize: 14, fontFamily: T.fontBody }}>
          A cleaner savings view with leaderboards, trend movement, and category breakdowns.
        </div>

        {renderNamedMetricCards([
          {
            title: "Total Savings Achieved",
            value: formatCurrencyCompact(data?.["Total Savings Achieved"]),
            accent: T.success,
          },
          {
            title: "Savings %",
            value: formatPercentCompact(savingsPercent),
            note: "Savings as a share of total spend",
            accent: T.brand,
          },
          {
            title: "Negotiation Savings",
            value: formatCurrencyCompact(data?.["Negotiation Savings"]),
            accent: T.warning,
          },
          {
            title: "Rate Contract Savings",
            value: formatCurrencyCompact(data?.["Rate Contract Savings"]),
            accent: T.purple,
          },
          {
            title: "Avoided Cost",
            value: formatCurrencyCompact(data?.["Cost Avoidance"]),
            accent: T.danger,
          },
        ], { gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" })}

        {/* <div style={{ marginBottom: 20 }}>
          {renderTable({ title: "RFQ-wise Savings Leaderboard", rows: data?.["RFQ-wise Savings"] })}
        </div> */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
            gap: 20,
            marginBottom: 20,
          }}
        >
          <NestedCard>
            <SubSectionHeader title="Monthly Savings Trend" />
            <div style={{ minHeight: 400 }}>
              <LineChart
                inline
                showHeading={false}
                dataLabel={mapArrayToChart(data?.["Monthly Savings"], "name", "amount").labels}
                dataValues={mapArrayToChart(data?.["Monthly Savings"], "name", "amount").values}
                yAxisValueFormatter={formatCurrencyCompact}
                tooltipValueFormatter={formatCurrencyCompact}
                chartDataSet={{
                  cardHeading: "Monthly Savings Trend",
                  chartLabels: ["Monthly Savings Trend"],
                  chartIndicators: [{ value: "Monthly", color: T.success }],
                  barColor: T.success,
                }}
                loadingState={loadingState}
                disableDuration={true}
                chartHeight={380}
              />
            </div>
          </NestedCard>
          {renderTrendChart({ title: "Yearly Savings Trend", rows: data?.["Yearly Savings"], accent: T.brand, chartHeight: 380, yAxisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact })}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            gap: 20,
            marginBottom: 20,
          }}
        >
          {renderArrayChart({ title: "Savings by Category", rows: data?.["Savings by Category"], accent: T.success, limit: 10, chartHeight: 380, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact, breakdownValueFormatter: formatCurrencyInCrores })}
          {renderArrayChart({ title: "Savings by Supplier", rows: data?.["Savings by Vendor"], accent: T.brand, limit: 10, chartHeight: 380, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact, breakdownValueFormatter: formatCurrencyInCrores })}
          {renderArrayChart({ title: "Savings by Department", rows: savingsByDepartment, accent: T.info, limit: 10, chartHeight: 380, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact, breakdownValueFormatter: formatCurrencyInCrores })}
          {renderArrayChart({ title: "Savings by Buyer", rows: data?.["Savings by Buyer"], accent: T.warning, limit: 10, chartHeight: 380, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact, breakdownValueFormatter: formatCurrencyInCrores })}
          {/* {renderArrayChart({ title: "Savings by Project", rows: data?.["Savings by Project"], accent: T.info, limit: 8, chartHeight: 380, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact })} */}
          {renderArrayChart({ title: "Savings by Location", rows: data?.["Savings by Location"], accent: "#14B8A6", limit: 10, chartHeight: 380, axisValueFormatter: formatCurrencyCompact, tooltipValueFormatter: formatCurrencyCompact, breakdownValueFormatter: formatCurrencyInCrores })}
        </div>
      </SectionCard>
    );
  };

  const renderDashboard4 = () => {
    const data = sections.dashboard4 || {};

    return (
      <SectionCard title="Sourcing Intelligence Center">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 14,
          }}
        >
          <MetricCard title="RFQs Floated" value={formatDisplayValue(data?.["RFQs Floated"])} accent={T.brand} onClick={resolveCardNavigation("RFQ")} />
          <MetricCard title="Quotations Received" value={formatDisplayValue(data?.["Quotations Received"])} accent={T.success} onClick={resolveCardNavigation("RFQ quotations")} />
          <MetricCard title="Vendor Participation %" value={formatDisplayValue(data?.["Vendor Participation %"])} accent={T.warning} onClick={resolveCardNavigation("Vendors supplier participation")} />
          <MetricCard title="Single Bid RFQs" value={formatDisplayValue(data?.["Single Bid RFQs"])} accent={T.purple} onClick={resolveCardNavigation("RFQ single bid")} />
          <MetricCard title="Multi Bid RFQs" value={formatDisplayValue(data?.["Multi Bid RFQs"])} accent={T.info} onClick={resolveCardNavigation("RFQ multi bid")} />
          <MetricCard title="Average Quotes per RFQ" value={formatDisplayValue(data?.["Average Quotes per RFQ"])} accent={T.danger} onClick={resolveCardNavigation("RFQ quotes")} />
          <MetricCard title="RFQ Cycle Time" value={formatDisplayValue(data?.["RFQ Cycle Time"])} accent="#14B8A6" onClick={resolveCardNavigation("RFQ cycle")} />
          <MetricCard title="Savings Generated" value={formatDisplayValue(data?.["Savings Generated"])} accent="#8B5CF6" onClick={resolveCardNavigation("RFQ savings")} />
        </div>

        <Divider />
        {renderArrayChart({
          title: "Supplier Win Ratio",
          rows: data?.["Vendor Win Ratio"],
          labelKey: "vendor_name",
          valueKey: "win_ratio",
          accent: T.brand,
          limit: 10,
          chartHeight: 400,
          valueSuffix: "%",
          fullWidth: true,
        })}
      </SectionCard>
    );
  };

  const renderDashboard5 = () => {
    const data = sections.dashboard5 || {};

    return (
      <SectionCard title="Supplier Performance Scorecard">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 14,
          }}
        >
          <MetricCard title="On-Time Delivery %" value={formatDisplayValue(data?.["On-Time Delivery %"])} accent={T.success} onClick={resolveCardNavigation("Supplier vendor delivery")} />
          <MetricCard title="Lead Time" value={formatDisplayValue(data?.["Lead Time"])} accent={T.brand} onClick={resolveCardNavigation("Supplier vendor lead time")} />
          <MetricCard title="Rejection %" value={formatDisplayValue(data?.["Rejection %"])} accent={T.danger} onClick={resolveCardNavigation("Supplier vendor rejection")} />
          <MetricCard title="Quality Score" value={formatDisplayValue(data?.["Quality Score"])} accent={T.warning} onClick={resolveCardNavigation("Supplier vendor quality")} />
          <MetricCard title="Responsiveness" value={formatDisplayValue(data?.["Responsiveness"])} accent={T.info} onClick={resolveCardNavigation("Supplier vendor responsiveness")} />
          <MetricCard title="Compliance Score" value={formatDisplayValue(data?.["Compliance Score"])} accent={T.purple} onClick={resolveCardNavigation("Supplier vendor compliance")} />
        </div>

        <Divider />
        {renderArrayChart({
          title: "Supplier Ranking",
          rows: data?.["Vendor Ranking"],
          labelKey: "vendor__company_name",
          valueKey: "wins",
          accent: T.brand,
          limit: 10,
          chartHeight: 400,
          fullWidth: true,
        })}

        <Divider />
        <NestedCard style={{ padding: 24 }}>
          <h3 style={{ margin: "0 0 18px", color: "#000000", fontSize: 22, fontFamily: T.fontDisplay, fontWeight: 600 }}>Supplier Delivery Performance</h3>
          {vendorDeliveryLoading ? (
            <div style={{ height: 340, display: "grid", placeItems: "center", color: "#000000", fontSize: 15 }}>Loading supplier performance...</div>
          ) : vendorDeliveryError ? (
            <div style={{ padding: 18, color: "#000000", fontSize: 15 }}>{vendorDeliveryError}</div>
          ) : vendorDeliveryRows.length ? (
            <div style={{ padding: 18, borderRadius: 18, background: "linear-gradient(145deg, #EFF6FF, #F0FDFA)", border: "1px solid #DBEAFE" }}>
              <div style={{ color: "#000000", fontSize: 16, fontWeight: 500, marginBottom: 16 }}>On-time delivery · 0–100% · {vendorDeliveryRows.length} suppliers</div>
              <div style={{ width: "100%", minWidth: 0, height: 420 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    accessibilityLayer
                    data={vendorDeliveryRows.slice((currentSupplierPage - 1) * 6, currentSupplierPage * 6).map((supplier) => ({
                      ...supplier,
                      on_time_percentage: supplier.on_time_percentage == null || supplier.on_time_percentage === "" ? null : Number(supplier.on_time_percentage),
                      average_delay_days: supplier.average_delay_days == null || supplier.average_delay_days === "" ? null : Number(supplier.average_delay_days),
                    }))}
                    margin={{ top: 24, right: 4, left: 0, bottom: 12 }}
                  >
                    <defs>
                      <linearGradient id="supplierDeliveryBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2DD4BF" />
                        <stop offset="100%" stopColor="#2563EB" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#DBEAFE" strokeDasharray="3 6" vertical={false} />
                    <XAxis dataKey="vendor_name" interval={0} axisLine={false} tickLine={false} tick={{ fill: "#000000", fontSize: 14, fontWeight: 500 }} height={64} angle={-20} textAnchor="end" tickMargin={14} tickFormatter={(name) => String(name || "Unknown").length > 12 ? `${String(name).slice(0, 11)}…` : name} />
                    <YAxis yAxisId="percentage" domain={[0, 100]} width={60} axisLine={false} tickLine={false} tick={{ fill: "#000000", fontSize: 14, fontWeight: 500 }} tickFormatter={(value) => `${value}%`} />
                    <YAxis yAxisId="days" orientation="right" width={60} axisLine={false} tickLine={false} tick={{ fill: "#000000", fontSize: 14, fontWeight: 500 }} tickFormatter={(value) => `${value}d`} />
                    <RechartsTooltip
                      cursor={{ fill: "rgba(37,99,235,0.05)" }}
                      labelStyle={{ color: "#000000", fontSize: 16, fontWeight: 600 }}
                      itemStyle={{ color: "#000000", fontSize: 15 }}
                      contentStyle={{ color: "#000000", fontSize: 15, border: "1px solid #DBEAFE", borderRadius: 14, boxShadow: "0 12px 32px rgba(15,23,42,0.12)" }}
                      formatter={(value, name) => [`${formatDisplayValue(value)}${name === "On-time delivery" ? "%" : " days"}`, name]}
                    />
                    <Legend verticalAlign="top" height={48} iconType="circle" wrapperStyle={{ fontSize: 15, fontWeight: 500 }} formatter={(value) => <span style={{ color: "#000000" }}>{value}</span>} />
                    <Bar yAxisId="percentage" dataKey="on_time_percentage" name="On-time delivery" fill="url(#supplierDeliveryBar)" radius={[8, 8, 0, 0]} maxBarSize={48} />
                    <Line yAxisId="days" dataKey="average_delay_days" name="Average delay" type="monotone" stroke="#F59E0B" strokeWidth={3} dot={{ r: 4, fill: "#FFFFFF", strokeWidth: 2 }} activeDot={{ r: 6 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
                <span style={{ fontSize: 15, color: "#000000" }}>Page {currentSupplierPage} of {supplierPerformancePages}</span>
                <div style={{ display: "flex", gap: 8 }}>
                  {[{ label: "Previous", page: currentSupplierPage - 1, disabled: currentSupplierPage === 1 }, { label: "Next", page: currentSupplierPage + 1, disabled: currentSupplierPage === supplierPerformancePages }].map((button) => (
                    <button key={button.label} type="button" aria-label={`${button.label} suppliers`} disabled={button.disabled} onClick={() => setSupplierPerformancePage(button.page)} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#FFFFFF", color: "#000000", fontSize: 15, fontWeight: 500, backgroundImage: button.disabled ? "linear-gradient(#F1F5F9, #F1F5F9)" : "none", cursor: button.disabled ? "not-allowed" : "pointer" }}>{button.label}</button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: 18, color: "#000000", fontSize: 15 }}>No supplier delivery performance available.</div>
          )}
        </NestedCard>
      </SectionCard>
    );
  };

  const renderDashboard9 = () => {
    const pageSize = 10;
    const totalPages = Math.max(1, Math.ceil(deliveryCount / pageSize));
    const statusTheme = (value) => {
      const status = String(value || "").toUpperCase();
      if (status.includes("DELAY") || status.includes("REJECT")) return { color: "#B91C1C", background: "#FEF2F2", border: "#FECACA" };
      if (status.includes("EARLY") || status.includes("FULLY") || status.includes("ON_TIME")) return { color: "#047857", background: "#ECFDF5", border: "#A7F3D0" };
      if (status.includes("PARTIAL") || status.includes("PENDING")) return { color: "#B45309", background: "#FFFBEB", border: "#FDE68A" };
      return { color: "#475569", background: "#F8FAFC", border: "#E2E8F0" };
    };
    const badge = (value) => {
      const theme = statusTheme(value);
      return (
        <span style={{ display: "inline-flex", padding: "5px 10px", borderRadius: 999, color: theme.color, background: theme.background, border: `1px solid ${theme.border}`, fontSize: 12, fontFamily: T.fontDisplay, whiteSpace: "nowrap" }}>
          {String(value || "—").replaceAll("_", " ")}
        </span>
      );
    };
    const date = (value) => value ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
    const columns = [
      ["po_number", "PO Number"], ["vendor_name", "Supplier"], ["po_date", "PO Date"], ["payment_date", "Payment Date"],
      ["expected_delivery_date", "Expected"], ["actual_received_date", "Received On"],
      ["ordered_quantity", "Ordered"], ["received_quantity", "Received"], ["pending_quantity", "Pending"],
      ["accepted_quantity", "Accepted"], ["rejected_quantity", "Rejected"], ["damaged_quantity", "Damaged"], ["short_quantity", "Short"],
      ["delivery_status", "Delivery Status"], ["fulfillment_status", "Fulfilment"], ["delay_days", "Delay"],
      ["invoice_number", "Invoice"], ["invoice_date", "Invoice Date"], ["invoice_uploaded_date", "Uploaded On"], ["grn_count", "GRNs"],
    ];

    return (
      <SectionCard title="PO Delivery Tracker">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14 }}>
          <MetricCard title="Total POs" value={formatDisplayValue(deliverySummary?.total_pos)} accent={T.brand} />
          <MetricCard title="Delivered" value={formatDisplayValue(deliverySummary?.delivered)} accent={T.success} />
          <MetricCard title="Pending" value={formatDisplayValue(deliverySummary?.pending)} accent={T.warning} />
          <MetricCard title="Delayed" value={formatDisplayValue(deliverySummary?.delayed)} accent={T.danger} />
          <MetricCard title="Partially Delivered" value={formatDisplayValue(deliverySummary?.partially_delivered)} accent={T.purple} />
          <MetricCard title="On-Time Delivery" value={`${normalizeMetricValue(deliverySummary?.on_time_percentage)}%`} accent={T.info} />
          <MetricCard title="Early Deliveries" value={formatDisplayValue(deliverySummary?.early)} accent="#0F766E" />
          <MetricCard title="Not Available" value={formatDisplayValue(deliverySummary?.not_available)} note={`${normalizeMetricValue(deliverySummary?.not_available_percentage)}%`} accent={T.textMuted} />
          <MetricCard title="Quantity Pending" value={formatDisplayValue(deliverySummary?.quantity_pending)} note={`${normalizeMetricValue(deliverySummary?.quantity_pending_percentage)}%`} accent={T.warning} />
        </div>

        <Divider />
        <NestedCard style={{ padding: 0, overflow: "hidden", border: "1px solid rgba(37,99,235,0.12)", boxShadow: "0 16px 45px rgba(15,23,42,0.07)" }}>
          <div style={{ padding: "20px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", borderBottom: `1px solid ${T.border}`, background: "linear-gradient(135deg, rgba(37,99,235,0.07), rgba(20,184,166,0.04))" }}>
            <div>
              <div style={{ fontSize: 18, fontFamily: T.fontDisplay, color: T.text }}>Purchase Order Deliveries</div>
              <div style={{ color: T.textMuted, fontSize: 13, marginTop: 3 }}>{deliveryCount.toLocaleString("en-IN")} purchase orders found</div>
            </div>
            <input
              type="search"
              value={deliverySearch}
              onChange={(event) => { setDeliverySearch(event.target.value); setDeliveryPage(1); }}
              placeholder="Search PO number..."
              aria-label="Search PO number"
              style={{ width: "min(100%, 320px)", height: 44, padding: "0 16px", borderRadius: 12, border: "1px solid rgba(37,99,235,0.24)", outline: "none", color: T.text, background: "rgba(255,255,255,0.94)", boxShadow: "0 5px 18px rgba(37,99,235,0.07)", fontFamily: T.fontBody }}
            />
          </div>

          <div style={{ overflowX: "auto", minHeight: 260 }}>
            <table style={{ width: "100%", minWidth: 3100, tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0, fontFamily: T.fontBody, fontSize: 13 }}>
              <colgroup>
                {columns.map(([key], index) => <col key={key} style={{ width: index === 0 ? 160 : index === 1 ? 240 : 150 }} />)}
              </colgroup>
              <thead>
                <tr style={{ background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)" }}>
                  {columns.map(([key, label], columnIndex) => <th key={key} style={{ position: "sticky", top: 0, left: columnIndex === 0 ? 0 : columnIndex === 1 ? 160 : undefined, zIndex: columnIndex < 2 ? 4 : 2, boxSizing: "border-box", padding: "14px 15px", textAlign: "left", color: columnIndex === 1 ? "#3730A3" : T.textSec, fontFamily: T.fontDisplay, whiteSpace: "nowrap", background: columnIndex === 0 ? "#DBEAFE" : columnIndex === 1 ? "#E0E7FF" : "#F5F8FC", borderBottom: "1px solid #DCE6F2" }}>{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {deliveryLoading ? (
                  <tr><td colSpan={columns.length} style={{ height: 210, textAlign: "center", color: T.textMuted }}>Loading purchase order deliveries...</td></tr>
                ) : deliveryError ? (
                  <tr><td colSpan={columns.length} style={{ height: 180, textAlign: "center", color: T.danger }}>{deliveryError}</td></tr>
                ) : deliveryRows.length ? deliveryRows.map((row, index) => (
                  <tr key={row?.po_id || `${row?.po_number}-${index}`} style={{ background: index % 2 ? "#FBFDFF" : "#FFFFFF" }}>
                    {columns.map(([key]) => {
                      let value = row?.[key];
                      if (["po_date", "payment_date", "expected_delivery_date", "actual_received_date", "invoice_date", "invoice_uploaded_date"].includes(key)) value = date(value);
                      if (["delivery_status", "fulfillment_status"].includes(key)) value = badge(value);
                      if (key === "delay_days") value = `${Number(value || 0)} day${Number(value || 0) === 1 ? "" : "s"}`;
                      if (key === "invoice_number") value = badge(row?.invoice_uploaded ? (value || "UPLOADED") : "NOT_UPLOADED");
                      const columnIndex = columns.findIndex(([columnKey]) => columnKey === key);
                      const quantityColor = key === "pending_quantity" || key === "rejected_quantity" || key === "damaged_quantity" || key === "short_quantity" ? T.danger : key === "received_quantity" || key === "accepted_quantity" ? "#047857" : T.textSec;
                      return <td key={key} style={{ position: columnIndex < 2 ? "sticky" : "static", left: columnIndex === 0 ? 0 : columnIndex === 1 ? 160 : undefined, zIndex: columnIndex < 2 ? 1 : 0, boxSizing: "border-box", overflow: "hidden", textOverflow: "ellipsis", padding: "15px", color: key === "vendor_name" ? "#312E81" : key === "po_number" ? T.heading : quantityColor, fontFamily: key === "po_number" || key === "vendor_name" || key.endsWith("quantity") ? T.fontDisplay : T.fontBody, whiteSpace: "nowrap", background: columnIndex === 0 ? "#EFF6FF" : columnIndex === 1 ? "#F5F7FF" : "inherit", borderBottom: "1px solid rgba(226,232,240,0.78)", boxShadow: columnIndex === 1 ? "8px 0 14px rgba(49,46,129,0.06)" : "none" }} title={typeof value === "string" ? value : undefined}>{value ?? "—"}</td>;
                    })}
                  </tr>
                )) : (
                  <tr><td colSpan={columns.length} style={{ height: 210, textAlign: "center", color: T.textMuted }}>No purchase order deliveries found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap", borderTop: `1px solid ${T.border}`, background: "#FBFDFF" }}>
            <span style={{ color: T.textMuted }}>Showing {deliveryCount ? (deliveryPage - 1) * pageSize + 1 : 0}–{Math.min(deliveryPage * pageSize, deliveryCount)} of <strong style={{ color: T.text }}>{deliveryCount}</strong> · Page {deliveryPage} of {totalPages}</span>
            <div style={{ display: "flex", gap: 10 }}>
              {[{ label: "Previous", next: Math.max(1, deliveryPage - 1), disabled: deliveryPage <= 1 }, { label: "Next", next: Math.min(totalPages, deliveryPage + 1), disabled: deliveryPage >= totalPages }].map((button) => (
                <button key={button.label} type="button" disabled={button.disabled || deliveryLoading} onClick={() => setDeliveryPage(button.next)} style={{ padding: "9px 16px", borderRadius: 10, border: "1px solid #CBD5E1", background: button.disabled ? "#F1F5F9" : "#FFFFFF", color: button.disabled ? "#94A3B8" : T.heading, fontFamily: T.fontDisplay, cursor: button.disabled ? "not-allowed" : "pointer" }}>{button.label}</button>
              ))}
            </div>
          </div>
        </NestedCard>
      </SectionCard>
    );
  };

  const renderDashboard6 = () => {
    const data = sections.dashboard6 || {};
    const touchlessBreakdown = data?.["Touchless Transactions Breakdown"] || {};
    const totalManual = normalizeMetricValue(data?.["Total Manual"]);
    const totalTouchless = normalizeMetricValue(data?.["Total Touchless and Autonomous"]);
    const totalAll = totalManual + totalTouchless;
    const touchlessEntries = Object.entries(touchlessBreakdown || {});
    const touchlessRows = touchlessEntries.map(([key, value]) => ({
      key,
      total: normalizeMetricValue(value?.Total),
      manual: normalizeMetricValue(value?.Manual),
      touchless: normalizeMetricValue(value?.["Touchless and Autonomous"]),
    }));
    const automationLabelMap = {
      PR: "Purchase Requisitions",
      PO: "Purchase Orders",
      "3-Way Matching": "3 Way Matching",
    };
    const automationPalette = ["#2563EB", "#0F766E", "#7C3AED", "#D97706", "#0891B2", "#16A34A", "#DB2777"];
    const automationRows = touchlessRows.map((row, index) => {
      const ratio = row.total > 0 ? (row.touchless / row.total) * 100 : 0;
      const manualRatio = row.total > 0 ? (row.manual / row.total) * 100 : 0;
      const route = resolveDashboardRoute(row.key);
      return {
        ...row,
        displayName: automationLabelMap[row.key] || row.key,
        ratio,
        manualRatio,
        accent: automationPalette[index % automationPalette.length],
        route,
      };
    });

    return (
      <SectionCard title="Autonomous Procurement Control Center">
        <div style={{ display: "grid", gap: 16 }}>
          <div>
            <SubSectionHeader title="KPIs" />
            <div style={{ marginBottom: 10, color: T.textMuted, fontSize: 13, fontFamily: T.fontBody }}>
              Touch means manual intervention. Touchless means autonomous execution.
            </div>
            {renderNamedMetricCards(
              [
                {
                  title: "Total Manual",
                  value: formatDisplayValue(totalManual),
                  accent: T.danger,
                },
                {
                  title: "Total Touchless and Autonomous",
                  value: formatDisplayValue(totalTouchless),
                  accent: T.success,
                },
                {
                  title: "Total All",
                  value: formatDisplayValue(totalAll),
                  accent: T.brand,
                },
                {
                  title: "Touchless Ratio",
                  value: formatPercentCompact(totalAll > 0 ? (totalTouchless / totalAll) * 100 : 0),
                  accent: T.info,
                },
              ],
              { gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" }
            )}
          </div>

          <div>
            <SubSectionHeader title="Touchless Transactions Breakdown" />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                gap: 16,
              }}
            >
              {automationRows.map((row) => (
                <div
                  key={row.key}
                  role={row.route ? "button" : undefined}
                  tabIndex={row.route ? 0 : undefined}
                  aria-label={row.route ? `Open ${row.displayName}` : row.displayName}
                  onClick={() => {
                    if (row.route) navigate(row.route);
                  }}
                  onKeyDown={(event) => {
                    if (!row.route || (event.key !== "Enter" && event.key !== " ")) return;
                    event.preventDefault();
                    navigate(row.route);
                  }}
                  style={{
                    background: "#FFFFFF",
                    border: `1px solid ${hexToRgba(row.accent, 0.2)}`,
                    borderRadius: 18,
                    padding: 18,
                    boxShadow: "0px 14px 32px rgba(15,23,42,0.08)",
                    display: "grid",
                    gap: 14,
                    minWidth: 0,
                    cursor: row.route ? "pointer" : "default",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: "0 0 auto 0",
                      height: 5,
                      background: `linear-gradient(90deg, ${row.accent}, ${hexToRgba(row.accent, 0.25)})`,
                      pointerEvents: "none",
                    }}
                  />
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            background: row.accent,
                            boxShadow: `0 0 0 5px ${hexToRgba(row.accent, 0.12)}`,
                            flexShrink: 0,
                          }}
                        />
                        <div
                          title={row.displayName}
                          style={{
                            fontSize: 17,
                            fontFamily: T.fontDisplay,
                            color: T.text,
                            lineHeight: 1.25,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {row.displayName}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6, fontFamily: T.fontDisplay }}>
                        Touch vs autonomous movement
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "7px 11px",
                        borderRadius: 999,
                        background: hexToRgba(row.accent, 0.12),
                        color: row.accent,
                        fontFamily: T.fontDisplay,
                        fontSize: 13,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatPercentCompact(row.ratio)}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr 1fr", gap: 10 }}>
                    {[
                      { label: "Total", value: row.total, color: T.text, background: "#F8FAFC", border: "rgba(148,163,184,0.2)" },
                      { label: "Touch", value: row.manual, color: "#B91C1C", background: "#FFF1F2", border: "rgba(244,63,94,0.2)" },
                      { label: "Touchless", value: row.touchless, color: "#047857", background: "#ECFDF5", border: "rgba(16,185,129,0.22)" },
                    ].map((item) => (
                      <div
                        key={item.label}
                        style={{
                          background: item.background,
                          border: `1px solid ${item.border}`,
                          borderRadius: 14,
                          padding: "11px 12px",
                          minWidth: 0,
                        }}
                      >
                        <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.fontDisplay }}>{item.label}</div>
                        <div
                          style={{
                            marginTop: 5,
                            fontSize: 18,
                            fontFamily: T.fontDisplay,
                            color: item.color,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatDisplayValue(item.value)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: "grid", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12, color: T.textMuted, fontFamily: T.fontDisplay }}>
                      <span>Touch {formatPercentCompact(row.manualRatio)}</span>
                      <span>Touchless {formatPercentCompact(row.ratio)}</span>
                    </div>
                    <div
                      style={{
                        height: 12,
                        borderRadius: 999,
                        background: "#E2E8F0",
                        overflow: "hidden",
                        display: "flex",
                        boxShadow: "inset 0 1px 2px rgba(15,23,42,0.08)",
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.max(0, Math.min(100, row.manualRatio))}%`,
                          height: "100%",
                          background: "linear-gradient(90deg, #FDA4AF 0%, #E11D48 100%)",
                        }}
                      />
                      <div
                        style={{
                          width: `${Math.max(0, Math.min(100, row.ratio))}%`,
                          height: "100%",
                          background: "linear-gradient(90deg, #34D399 0%, #059669 100%)",
                        }}
                      />
                    </div>
                  </div>
                  {row.route ? (
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "7px 10px",
                          borderRadius: 999,
                          background: hexToRgba(row.accent, 0.09),
                          border: `1px solid ${hexToRgba(row.accent, 0.14)}`,
                          color: row.accent,
                          fontSize: 12,
                          fontFamily: T.fontDisplay,
                        }}
                      >
                        Open module <span aria-hidden="true">-&gt;</span>
                      </span>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>
    );
  };

  const renderDashboard7 = () => {
    const data = sections.dashboard7 || {};
    const staticRows = Array.isArray(data?.Static) ? data.Static : [];
    const savingsRows = Array.isArray(data?.Savings) ? data.Savings : [];
    const noSavingsRows = Array.isArray(data?.["No savings"]) ? data["No savings"] : [];
    const allRows = [...staticRows, ...savingsRows, ...noSavingsRows];

    const getAbsDifference = (row) =>
      Math.abs(
        normalizeMetricValue(
          row?.price_movement?.difference_display ?? row?.price_movement?.difference ?? 0
        )
      );

    const aggregateByLabel = (rows = [], getLabel = (row) => row?.name, getValue = () => 1, limit = 6) => {
      const bucket = new Map();
      rows.forEach((row) => {
        const label = String(getLabel(row) ?? "").trim() || "Unmapped";
        const value = normalizeMetricValue(getValue(row));
        bucket.set(label, (bucket.get(label) || 0) + value);
      });
      const list = Array.from(bucket.entries())
        .map(([name, amount]) => ({ name, amount }))
        .sort((a, b) => b.amount - a.amount);
      if (list.length <= limit) return list;
      const othersItems = list.slice(limit);
      const othersAmount = othersItems.reduce((sum, row) => sum + row.amount, 0);
      return [
        ...list.slice(0, limit),
        {
          name: "Others",
          amount: othersAmount,
          __isOthers: true,
          __othersItems: othersItems.map((row) => ({
            label: row.name,
            value: row.amount,
          })),
        },
      ];
    };

    const statusRows = [
      { name: "Savings", amount: savingsRows.length },
      { name: "Static", amount: staticRows.length },
      { name: "No savings", amount: noSavingsRows.length },
    ].filter((row) => row.amount > 0);

    const categoryMixRows =
      purchaseMovementCategoryMode === "nonAddressable"
        ? allRows.filter((row) => isNonAddressablePurchaseMovementItem(row))
        : allRows.filter((row) => !isNonAddressablePurchaseMovementItem(row));

    const categoryRows = aggregateByLabel(
      categoryMixRows,
      (row) => row?.item?.category || row?.item?.name || "Unmapped",
      () => 1,
      6
    );

    const savingsBarRows = savingsRows
      .map((row) => ({
        name: row?.item?.name || row?.item?.code || "Unnamed Item",
        amount: getAbsDifference(row),
        itemName: row?.item?.name || row?.item?.code || "Unnamed Item",
        previousPrice: normalizeMetricValue(
          row?.previous_purchase_order?.unit_price_display ?? row?.previous_purchase_order?.unit_price
        ),
        latestPrice: normalizeMetricValue(
          row?.latest_purchase_order?.unit_price_display ?? row?.latest_purchase_order?.unit_price
        ),
        category: row?.item?.category || "Unmapped",
        previousVendor: row?.previous_purchase_order?.vendor_name || "-",
        latestVendor: row?.latest_purchase_order?.vendor_name || "-",
        rfq: row?.latest_purchase_order?.rfq_code || row?.previous_purchase_order?.rfq_code || "-",
      }))
      .sort((a, b) => b.amount - a.amount);

    const topSavingsRows = savingsBarRows.slice(0, 8);
    const priceComparisonRows = savingsBarRows.slice(0, 8).map((row) => ({
      label: row.name,
      itemName: row.itemName,
      previous: row.previousPrice,
      latest: row.latestPrice,
      difference: row.amount,
    }));

    const statusTotal = statusRows.reduce((sum, row) => sum + row.amount, 0);
    const categoryTotal = categoryRows.reduce((sum, row) => sum + row.amount, 0);
    const totalSavings = savingsBarRows.reduce((sum, row) => sum + row.amount, 0);
    const averageSavings = savingsBarRows.length ? totalSavings / savingsBarRows.length : 0;

    const donutColors = [T.success, T.brand, T.warning];
    const pieColors = [T.info, T.purple, T.success, T.warning, T.brand, "#60A5FA"];
    const lineTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const previous = payload.find((item) => item?.dataKey === "previous");
      const latest = payload.find((item) => item?.dataKey === "latest");
      const difference = payload.find((item) => item?.dataKey === "difference");
      const itemName = payload?.[0]?.payload?.itemName || label;
      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid rgba(226,232,240,0.92)",
            boxShadow: "0px 12px 30px rgba(15,23,42,0.12)",
            borderRadius: 12,
            padding: "10px 12px",
            fontFamily: T.fontBody,
            fontSize: 14,
            color: T.text,
            minWidth: 220,
          }}
        >
          <div style={{ color: T.textMuted, marginBottom: 6 }}>{itemName}</div>
          <div style={{ display: "grid", gap: 4 }}>
            <div>
              <span style={{ color: T.textMuted }}>Previous: </span>
              <strong>{formatCurrencyCompact(previous?.value || 0)}</strong>
            </div>
            <div>
              <span style={{ color: T.textMuted }}>Latest: </span>
              <strong>{formatCurrencyCompact(latest?.value || 0)}</strong>
            </div>
            <div>
              <span style={{ color: T.textMuted }}>Difference: </span>
              <strong>{formatCurrencyCompact(difference?.value || 0)}</strong>
            </div>
          </div>
        </div>
      );
    };

    const detailRows = [
      ...savingsBarRows.map((row) => ({
        ...row,
        status: "Savings",
      })),
      ...staticRows.map((row) => ({
        name: row?.item?.name || row?.item?.code || "Unnamed Item",
        amount: 0,
        itemName: row?.item?.name || row?.item?.code || "Unnamed Item",
        previousPrice: normalizeMetricValue(
          row?.previous_purchase_order?.unit_price_display ?? row?.previous_purchase_order?.unit_price
        ),
        latestPrice: normalizeMetricValue(
          row?.latest_purchase_order?.unit_price_display ?? row?.latest_purchase_order?.unit_price
        ),
        category: row?.item?.category || "Unmapped",
        previousVendor: row?.previous_purchase_order?.vendor_name || "-",
        latestVendor: row?.latest_purchase_order?.vendor_name || "-",
        status: "Static",
      })),
      ...noSavingsRows.map((row) => ({
        name: row?.item?.name || row?.item?.code || "Unnamed Item",
        amount: 0,
        itemName: row?.item?.name || row?.item?.code || "Unnamed Item",
        previousPrice: normalizeMetricValue(
          row?.previous_purchase_order?.unit_price_display ?? row?.previous_purchase_order?.unit_price
        ),
        latestPrice: normalizeMetricValue(
          row?.latest_purchase_order?.unit_price_display ?? row?.latest_purchase_order?.unit_price
        ),
        category: row?.item?.category || "Unmapped",
        previousVendor: row?.previous_purchase_order?.vendor_name || "-",
        latestVendor: row?.latest_purchase_order?.vendor_name || "-",
        status: "No savings",
      })),
    ]
      .sort((a, b) => b.amount - a.amount)
      .map((row) => ({
        Item: row.itemName,
        Category: row.category,
        "Previous Vendor": row.previousVendor,
        "Latest Vendor": row.latestVendor,
        Status: row.status,
        "Previous Unit Price": formatCurrencyCompact(row.previousPrice),
        "Latest Unit Price": formatCurrencyCompact(row.latestPrice),
        Difference: formatCurrencyCompact(row.amount),
      }));

    const purchaseMovementPageSize = 10;
    const purchaseMovementPageCount = Math.max(1, Math.ceil(detailRows.length / purchaseMovementPageSize));
    const purchaseMovementCurrentPage = Math.min(purchaseMovementPageNumber, purchaseMovementPageCount);
    const purchaseMovementPageRows = detailRows.slice(
      (purchaseMovementCurrentPage - 1) * purchaseMovementPageSize,
      purchaseMovementCurrentPage * purchaseMovementPageSize
    );

    return (
      <SectionCard title="Purchase Price Benchmarking">
        <div style={{ marginBottom: 16, color: T.textMuted, fontSize: 14, fontFamily: T.fontBody }}>
          Price movement across previous and latest purchase orders, with movement status, item savings, and unit price comparison.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 14,
            marginBottom: 18,
          }}
        >
          <MetricCard title="Total Items" value={String(allRows.length)} note="All movement records" accent={T.brand} onClick={resolveCardNavigation("Items price benchmarking")} />
          <MetricCard title="Savings Items" value={String(savingsRows.length)} note="Positive movement" accent={T.success} onClick={resolveCardNavigation("Items price savings")} />
          <MetricCard title="Static Items" value={String(staticRows.length)} note="No change" accent={T.info} onClick={resolveCardNavigation("Items price static")} />
          <MetricCard title="No Savings Items" value={String(noSavingsRows.length)} note="Price not improved" accent={T.warning} onClick={resolveCardNavigation("Items price no savings")} />
          <MetricCard title="Total Savings" value={formatCurrencyCompact(totalSavings)} note="Absolute savings value" accent={T.purple} onClick={resolveCardNavigation("price list savings")} />
          <MetricCard title="Average Savings" value={formatCurrencyCompact(averageSavings)} note="Average per savings item" accent="#14B8A6" onClick={resolveCardNavigation("price list savings")} />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
            gap: 16,
            marginBottom: 16,
          }}
        >
          <NestedCard style={{ minHeight: 430 }}>
            <SubSectionHeader title="Price Movement Split" />
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <defs>
                    <linearGradient id="dashboard7DonutGreen" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor={T.success} stopOpacity={0.98} />
                      <stop offset="100%" stopColor="#7EE7D0" stopOpacity={0.92} />
                    </linearGradient>
                    <linearGradient id="dashboard7DonutBlue" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor={T.brand} stopOpacity={0.98} />
                      <stop offset="100%" stopColor="#88A6FF" stopOpacity={0.92} />
                    </linearGradient>
                    <linearGradient id="dashboard7DonutAmber" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor={T.warning} stopOpacity={0.98} />
                      <stop offset="100%" stopColor="#FCD37C" stopOpacity={0.92} />
                    </linearGradient>
                  </defs>
                  <Pie
                    data={statusRows}
                    dataKey="amount"
                    nameKey="name"
                    innerRadius={78}
                    outerRadius={112}
                    paddingAngle={4}
                    stroke="rgba(255,255,255,0.92)"
                    strokeWidth={2}
                    isAnimationActive
                    animationDuration={400}
                  >
                    {statusRows.map((entry, index) => (
                      <Cell
                        key={`status-${index}`}
                        fill={`url(#${["dashboard7DonutGreen", "dashboard7DonutBlue", "dashboard7DonutAmber"][index % 3]})`}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    content={(props) => (
                      <CustomChartTooltip {...props} title="Price Movement Split" showPercent total={statusTotal} />
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <RechartsLegend
              rows={statusRows.map((row) => ({ label: row.name }))}
              colors={donutColors}
            />
          </NestedCard>

          <NestedCard style={{ minHeight: 430 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <SubSectionHeader title="Category Mix" />
              <RadioGroup
                row
                value={purchaseMovementCategoryMode}
                onChange={(e) => setPurchaseMovementCategoryMode(e.target.value)}
                sx={{
                  marginTop: "-2px",
                  "& .MuiFormControlLabel-label": {
                    fontFamily: T.fontDisplay,
                    fontSize: 13,
                    color: T.textSec,
                  },
                }}
              >
                <FormControlLabel
                  value="addressable"
                  control={<Radio size="small" />}
                  label="Addressable"
                />
                <FormControlLabel
                  value="nonAddressable"
                  control={<Radio size="small" />}
                  label="Non Addressable"
                />
              </RadioGroup>
            </div>
            <div style={{ marginBottom: 12, color: T.textMuted, fontSize: 13, fontFamily: T.fontBody }}>
              {/* {purchaseMovementCategoryMode === "addressable"
                ? "Addressable categories are shown here."
                : "Non-addressable categories such as office rent, electricity, housekeeping, security, maintenance, taxes and drinking water are shown here."} */}
            </div>
            <div style={{ width: "100%", height: 260 }}>
              {categoryRows.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      <linearGradient id="dashboard7CategoryGradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={T.info} stopOpacity={0.98} />
                        <stop offset="100%" stopColor="#9EDDF7" stopOpacity={0.92} />
                      </linearGradient>
                    </defs>
                    <Pie
                      data={categoryRows}
                      dataKey="amount"
                      nameKey="name"
                      outerRadius={112}
                      innerRadius={0}
                      paddingAngle={2}
                      stroke="rgba(255,255,255,0.92)"
                      strokeWidth={2}
                      isAnimationActive
                      animationDuration={400}
                      onClick={(entry, _index, event) => {
                        if (!Array.isArray(entry?.__othersItems) || entry.__othersItems.length === 0) {
                          setPinnedOthersTooltip(null);
                          return;
                        }

                        const nativeEvent = event?.nativeEvent || event;
                        setPinnedOthersTooltip({
                          visible: true,
                          x: nativeEvent?.clientX || 0,
                          y: nativeEvent?.clientY || 0,
                          title: entry?.name || "Others",
                          value: formatCurrencyInCrores(entry?.amount ?? 0),
                          breakdown: entry.__othersItems,
                        });
                      }}
                    >
                      {categoryRows.map((entry, index) => (
                        <Cell
                          key={`category-${index}`}
                          fill={entry.__isOthers ? "#CBD5E1" : pieColors[index % pieColors.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      content={(props) => (
                        <CustomChartTooltip
                          {...props}
                          title="Category Mix"
                          showPercent
                          total={categoryTotal}
                          valueFormatter={formatTooltipValue}
                          breakdownValueFormatter={formatCurrencyInCrores}
                        />
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: T.textMuted,
                    fontFamily: T.fontBody,
                    fontSize: 14,
                    textAlign: "center",
                    padding: "0 16px",
                  }}
                >
                  No {purchaseMovementCategoryMode === "addressable" ? "addressable" : "non-addressable"} items found.
                </div>
              )}
            </div>
            {categoryRows.length > 0 ? (
              <RechartsLegend
                rows={categoryRows.map((row) => ({ label: row.name }))}
                colors={pieColors}
              />
            ) : null}
            {pinnedOthersTooltip?.visible ? (
              <div
                ref={pinnedOthersTooltipRef}
                style={{
                  position: "fixed",
                  left: pinnedOthersTooltipPosition.left,
                  top: pinnedOthersTooltipPosition.top,
                  zIndex: 80,
                  minWidth: 220,
                  maxWidth: 360,
                  background: "#FFFFFF",
                  border: "1px solid rgba(226,232,240,0.95)",
                  borderRadius: 16,
                  boxShadow: "0px 18px 40px rgba(15,23,42,0.18)",
                  padding: "12px 14px",
                  color: T.text,
                  fontFamily: T.fontBody,
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 6,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        color: T.textMuted,
                        fontSize: 12.5,
                        marginBottom: 4,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {pinnedOthersTooltip.title}
                    </div>
                    <div style={{ fontFamily: T.fontDisplay, fontSize: 13 }}>
                      {pinnedOthersTooltip.value}
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-label="Close tooltip"
                    onClick={() => setPinnedOthersTooltip(null)}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: T.textMuted,
                      cursor: "pointer",
                      fontSize: 16,
                      lineHeight: 1,
                      padding: 0,
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
                <div
                  style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: "1px solid rgba(226,232,240,0.92)",
                    display: "grid",
                    gap: 8,
                    maxHeight: 220,
                    overflowY: "auto",
                  }}
                >
                  {pinnedOthersTooltip.breakdown.map((entry, index) => (
                    <div
                      key={`${entry?.label || "item"}-${index}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: 12,
                        alignItems: "start",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12.5,
                          color: T.text,
                          lineHeight: 1.35,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {entry?.label || "-"}
                      </div>
                      <div
                        style={{
                          fontSize: 12.5,
                          color: T.text,
                          fontFamily: T.fontDisplay,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatCurrencyInCrores(entry?.value ?? 0)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </NestedCard>

          <NestedCard style={{ minHeight: 430 }}>
            <SubSectionHeader title="Top Savings Items" />
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topSavingsRows}
                  layout="vertical"
                  margin={{ top: 10, right: 18, left: 6, bottom: 8 }}
                >
                  <defs>
                    <linearGradient id="dashboard7SavingsGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={T.success} stopOpacity={0.96} />
                      <stop offset="100%" stopColor="#7EE7D0" stopOpacity={0.96} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(148,163,184,0.15)" strokeDasharray="3 4" horizontal={false} />
                  <XAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: T.textMuted, fontFamily: T.fontBody, fontSize: 14 }}
                    tickFormatter={(value) => formatCurrencyCompact(value)}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    width={170}
                    interval={0}
                    tick={(props) => <RechartsWrappedYAxisTick {...props} width={160} />}
                  />
                  <RechartsTooltip content={(props) => <CustomChartTooltip {...props} title="Top Savings Items" />} />
                  <Bar
                    dataKey="amount"
                    radius={[0, 12, 12, 0]}
                    barSize={18}
                    fill="url(#dashboard7SavingsGradient)"
                    isAnimationActive
                    animationDuration={400}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </NestedCard>

          <NestedCard style={{ minHeight: 430 }}>
            <SubSectionHeader title="Previous vs Latest Unit Price" />
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RechartsLineChart data={priceComparisonRows} margin={{ top: 10, right: 20, left: 8, bottom: 8 }}>
                  <CartesianGrid stroke="rgba(148,163,184,0.15)" strokeDasharray="3 4" vertical={false} />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    height={88}
                    tick={(props) => <RechartsAngledTick {...props} angle={-30} />}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: T.textMuted, fontFamily: T.fontBody, fontSize: 14 }}
                    tickFormatter={(value) => formatCurrencyCompact(value)}
                  />
                  <Legend iconType="circle" wrapperStyle={{ paddingTop: 8, fontSize: 13, fontFamily: T.fontBody }} />
                  <RechartsTooltip content={lineTooltip} />
                  <Line
                    type="monotone"
                    dataKey="previous"
                    stroke={T.brand}
                    strokeWidth={3}
                    dot={{ r: 3, strokeWidth: 2, fill: "#fff" }}
                    activeDot={{ r: 5 }}
                    isAnimationActive
                    animationDuration={400}
                  />
                  <Line
                    type="monotone"
                    dataKey="latest"
                    stroke={T.success}
                    strokeWidth={3}
                    dot={{ r: 3, strokeWidth: 2, fill: "#fff" }}
                    activeDot={{ r: 5 }}
                    isAnimationActive
                    animationDuration={400}
                  />
                </RechartsLineChart>
              </ResponsiveContainer>
            </div>
          </NestedCard>
        </div>

        <Divider />
        {renderTable({
          title: "Purchase Price Benchmarking Detail",
          rows: detailRows,
          pagination: {
            rows: purchaseMovementPageRows,
            pageNumber: purchaseMovementCurrentPage,
            itemsPerPage: detailRows.length,
            totalItems: purchaseMovementPageCount,
            setPageNumber: setPurchaseMovementPageNumber,
            previouPageClick: () =>
              setPurchaseMovementPageNumber((current) => Math.max(1, Number(current || 1) - 1)),
            nextPageClick: () =>
              setPurchaseMovementPageNumber((current) => Math.min(purchaseMovementPageCount, Number(current || 1) + 1)),
            disablePrevious: purchaseMovementCurrentPage <= 1,
            disableNext: purchaseMovementCurrentPage >= purchaseMovementPageCount,
          },
        })}
      </SectionCard>
    );
  };

  const renderDashboard8 = () => {
    const data = assetSummary || sections.dashboard8 || {};
    const totalAssets = normalizeMetricValue(data?.total_assets);
    const totalValueLabel =
      data?.total_value_inr_label ||
      (Number.isFinite(Number(data?.total_value_inr))
        ? `\u20B9 ${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(data.total_value_inr))}`
        : "\u20B90");
    const totalValueFormatted = data?.total_value_inr_formatted || "";
    const inWarranty = normalizeMetricValue(data?.in_warranty);
    const inWarrantyPct = normalizeMetricValue(data?.in_warranty_percentage);
    const outWarranty = normalizeMetricValue(data?.out_of_warranty);
    const outWarrantyPct = normalizeMetricValue(data?.out_of_warranty_percentage);
    const activeAssets = normalizeMetricValue(data?.active_assets);
    const activeAssetsPct = normalizeMetricValue(data?.active_assets_percentage);
    const unknownWarranty = normalizeMetricValue(data?.unknown_warranty);
    const purchaseValue = normalizeMetricValue(data?.total_purchase_value_inr);
    const bookValue = normalizeMetricValue(data?.total_book_value_inr);
    const depreciationValue = normalizeMetricValue(data?.total_accumulated_depreciation_inr);
    const formatExactInr = (value) =>
      `₹ ${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(normalizeMetricValue(value))}`;
    const valueCards = [
      {
        title: "Total Purchase Value",
        value: purchaseValue > 0 ? formatCurrencyCompact(purchaseValue) : "₹0",
        note: purchaseValue > 0 ? formatExactInr(purchaseValue) : "Gross purchase value",
        accent: T.brand,
      },
      {
        title: "Total Book Value",
        value: bookValue > 0 ? formatCurrencyCompact(bookValue) : "₹0",
        note: bookValue > 0 ? formatExactInr(bookValue) : "Net book value",
        accent: T.success,
      },
      {
        title: "Accumulated Depreciation",
        value: depreciationValue > 0 ? formatCurrencyCompact(depreciationValue) : "₹0",
        note: depreciationValue > 0 ? formatExactInr(depreciationValue) : "Total depreciation",
        accent: T.warning,
      },
    ];
    const warrantyRows = [
      { name: "In Warranty", amount: inWarranty },
      { name: "Out of Warranty", amount: outWarranty },
    ].filter((row) => row.amount > 0);
    const warrantyTotal = warrantyRows.reduce((sum, row) => sum + row.amount, 0);
    const warrantyColors = [T.success, T.warning];
    const loadingLabel = assetSummaryLoading ? "Loading asset summary..." : "";

    return (
      <SectionCard title="Procurement Insights">
        <div style={{ marginBottom: 16, color: T.textMuted, fontSize: 14, fontFamily: T.fontBody }}>
          Asset book value, warranty split, and active asset health pulled from <strong>get_asset_summary</strong>.
        </div>

        {assetSummaryError ? (
          <div
            style={{
              marginBottom: 16,
              padding: "12px 14px",
              borderRadius: 14,
              border: "1px solid rgba(239,68,68,0.2)",
              background: "rgba(239,68,68,0.06)",
              color: "#991B1B",
              fontFamily: T.fontBody,
              fontSize: 13,
            }}
          >
            {assetSummaryError}
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 14,
            marginBottom: 18,
          }}
        >
          <MetricCard
            title="Total Assets"
            value={assetSummaryLoading ? "..." : totalAssets.toLocaleString("en-IN")}
            note="All registered assets"
            accent={T.brand}
            onClick={() => navigate("/Buyer_dashboard/Asset_Register")}
          />
          <MetricCard
            title="Total Value"
            value={assetSummaryLoading ? "..." : totalValueLabel}
            note={assetSummaryLoading ? loadingLabel : totalValueFormatted ? `\u20B9 ${totalValueFormatted}` : "Book value in INR"}
            accent={T.purple}
            onClick={() => navigate("/Buyer_dashboard/Asset_Register")}
          />
          <MetricCard
            title="Active Assets"
            value={assetSummaryLoading ? "..." : activeAssets.toLocaleString("en-IN")}
            note={assetSummaryLoading ? loadingLabel : `${activeAssetsPct}% active`}
            accent={T.success}
            onClick={() => navigate("/Buyer_dashboard/Asset_Register")}
          />
          <MetricCard
            title="In Warranty"
            value={assetSummaryLoading ? "..." : inWarranty.toLocaleString("en-IN")}
            note={assetSummaryLoading ? loadingLabel : `${inWarrantyPct}% of assets`}
            accent={T.info}
            onClick={() => navigate("/Buyer_dashboard/Asset_Register")}
          />
          <MetricCard
            title="Out of Warranty"
            value={assetSummaryLoading ? "..." : outWarranty.toLocaleString("en-IN")}
            note={assetSummaryLoading ? loadingLabel : `${outWarrantyPct}% of assets`}
            accent={T.warning}
            onClick={() => navigate("/Buyer_dashboard/Asset_Register")}
          />
          <MetricCard
            title="Unknown Warranty"
            value={assetSummaryLoading ? "..." : unknownWarranty.toLocaleString("en-IN")}
            note="Needs classification"
            accent={T.danger}
            onClick={() => navigate("/Buyer_dashboard/Asset_Register")}
          />
        </div>

        <NestedCard style={{ marginBottom: 18 }}>
          <SubSectionHeader title="Asset Value" />
          <div style={{ marginBottom: 12, color: T.textMuted, fontSize: 13, fontFamily: T.fontBody }}>
            Purchase value, book value, and accumulated depreciation from the asset register summary.
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12,
            }}
          >
            {valueCards.map((card) => (
              <MetricCard
                key={card.title}
                title={card.title}
                value={assetSummaryLoading ? "..." : card.value}
                note={assetSummaryLoading ? loadingLabel : card.note}
                accent={card.accent}
                onClick={() => navigate("/Buyer_dashboard/Asset_Register")}
              />
            ))}
          </div>
        </NestedCard>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            gap: 16,
            marginBottom: 16,
          }}
        >
          <NestedCard style={{ minHeight: 420 }}>
            <SubSectionHeader title="Warranty Split" />
            <div style={{ marginBottom: 10, color: T.textMuted, fontSize: 13, fontFamily: T.fontBody }}>
              Active warranty coverage across the current asset base.
            </div>
            <div style={{ width: "100%", height: 260 }}>
              {warrantyRows.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={warrantyRows}
                      dataKey="amount"
                      nameKey="name"
                      innerRadius={78}
                      outerRadius={112}
                      paddingAngle={4}
                      stroke="rgba(255,255,255,0.92)"
                      strokeWidth={2}
                      isAnimationActive
                      animationDuration={400}
                    >
                      {warrantyRows.map((entry, index) => (
                        <Cell key={`asset-warranty-${index}`} fill={warrantyColors[index % warrantyColors.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      content={(props) => (
                        <CustomChartTooltip {...props} title="Warranty Split" showPercent total={warrantyTotal} />
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: T.textMuted,
                    fontFamily: T.fontBody,
                    fontSize: 14,
                  }}
                >
                  {assetSummaryLoading ? "Loading warranty split..." : "No warranty data available."}
                </div>
              )}
            </div>
            {warrantyRows.length > 0 ? <RechartsLegend rows={warrantyRows.map((row) => ({ label: row.name }))} colors={warrantyColors} /> : null}
          </NestedCard>

          <NestedCard style={{ minHeight: 420 }}>
            <SubSectionHeader title="Asset Health Snapshot" />
            <div style={{ marginBottom: 12, color: T.textMuted, fontSize: 13, fontFamily: T.fontBody }}>
              A quick read on asset posture, value concentration, and data completeness.
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "linear-gradient(180deg, #f9fbff 0%, #ffffff 100%)",
                  border: `1px solid ${T.border}`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                  <span style={{ color: T.textSec, fontSize: 13, fontFamily: T.fontDisplay }}>Active Coverage</span>
                  <span style={{ color: T.text, fontSize: 13, fontFamily: T.fontDisplay }}>{activeAssetsPct}%</span>
                </div>
                <div style={{ height: 10, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}>
                  <div
                    style={{
                      width: `${Math.max(0, Math.min(100, activeAssetsPct))}%`,
                      height: "100%",
                      borderRadius: 999,
                      background: `linear-gradient(90deg, ${T.success}, rgba(255,255,255,0.6))`,
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "linear-gradient(180deg, #f9fbff 0%, #ffffff 100%)",
                  border: `1px solid ${T.border}`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                  <span style={{ color: T.textSec, fontSize: 13, fontFamily: T.fontDisplay }}>Warranty Coverage</span>
                  <span style={{ color: T.text, fontSize: 13, fontFamily: T.fontDisplay }}>
                    {inWarrantyPct}% / {outWarrantyPct}%
                  </span>
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ color: T.textMuted, fontSize: 12 }}>In Warranty</span>
                      <span style={{ color: T.text, fontSize: 12, fontFamily: T.fontDisplay }}>{inWarranty.toLocaleString("en-IN")}</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}>
                      <div style={{ width: `${Math.max(0, Math.min(100, inWarrantyPct))}%`, height: "100%", background: T.success }} />
                    </div>
                  </div>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ color: T.textMuted, fontSize: 12 }}>Out of Warranty</span>
                      <span style={{ color: T.text, fontSize: 12, fontFamily: T.fontDisplay }}>{outWarranty.toLocaleString("en-IN")}</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}>
                      <div style={{ width: `${Math.max(0, Math.min(100, outWarrantyPct))}%`, height: "100%", background: T.warning }} />
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(37,99,235,0.04)",
                  border: "1px solid rgba(37,99,235,0.12)",
                  color: T.textSec,
                  fontSize: 13,
                  fontFamily: T.fontBody,
                }}
              >
                Unknown warranty assets: <strong style={{ color: T.text }}>{unknownWarranty.toLocaleString("en-IN")}</strong>. Open the Asset Register for the full asset-level view.
              </div>
            </div>
          </NestedCard>
        </div>
      </SectionCard>
    );
  };

  const activeRenderer = {
    dashboard1: renderDashboard1,
    dashboard2: renderDashboard2,
    dashboard3: renderDashboard3,
    dashboard4: renderDashboard4,
    dashboard5: renderDashboard5,
    dashboard6: renderDashboard6,
    dashboard7: renderDashboard7,
    dashboard8: renderDashboard8,
    dashboard9: renderDashboard9,
  }[activeSection];

  return (
    <div
      className={classes.dashboardPage}
      style={{
        fontFamily: T.fontBody,
        fontSize: 14,
        lineHeight: 1.6,
        background: T.bg,
      }}
    >
      <div className={classes.pageSectionPadMain} style={{ paddingTop: 20 }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 30, fontFamily: T.fontDisplay, color: T.heading, letterSpacing: "-0.03em" }}>
            Procurement Command Center
          </div>
          {/* <div style={{ fontSize: 14, fontFamily: T.fontBody, color: T.textMuted, marginTop: 6, maxWidth: 840 }}>
            A clean procurement SaaS overview. Use the navigation bar to switch between dashboards and view one section at a time.
          </div> */}
        </div>

        <div
          className={classes.tabNavInner}
          style={{
            marginBottom: 18,
            position: "sticky",
            top: 12,
            zIndex: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
            <div className={classes.tabNavLabel} style={{ marginBottom: 0 }}>DASHBOARD SECTIONS</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[{ label: "Scroll dashboard sections left", icon: "‹", direction: -1 }, { label: "Scroll dashboard sections right", icon: "›", direction: 1 }].map((control) => (
                <button
                  key={control.direction}
                  type="button"
                  aria-label={control.label}
                  title={control.label}
                  onClick={() => scrollDashboardTabs(control.direction)}
                  style={{ width: 38, height: 38, display: "grid", placeItems: "center", borderRadius: 12, border: "1px solid #CBD5E1", background: "linear-gradient(180deg, #FFFFFF 0%, #F1F5F9 100%)", color: "#0F172A", boxShadow: "0 5px 14px rgba(15,23,42,0.09)", fontSize: 28, lineHeight: 1, fontFamily: "Arial, sans-serif", cursor: "pointer" }}
                >
                  {control.icon}
                </button>
              ))}
            </div>
          </div>
          <div ref={tabNavScrollRef} className={classes.tabNavTabs}>
            {BUYER_ANALYTICS_MENU.map((tab) => {
              const isActive = activeSection === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveSection(tab.id)}
                  className={`${classes.tabBtn} ${isActive ? classes.tabBtnActive : ""}`}
                  style={{
                    "--tab-accent": tab.accent,
                    scrollSnapAlign: "start",
                  }}
                >
                  <div className={classes.tabBtnTop}>
                    <span className={classes.tabBtnDot} />
                    <span className={classes.tabBtnLabel}>{tab.label}</span>
                  </div>
                  <div className={classes.tabBtnDesc}>{tab.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {activeSection === "dashboard1" ? <div style={{ marginBottom: 18 }}>{renderExecutiveSummary()}</div> : null}

        {activeRenderer ? activeRenderer() : null}
      </div>
    </div>
  );
};

export default NewBuyerDashboardV2;
